// DispatchDeck - Payroll View (TASK-850 slice B — company onboarding)
// DD Payroll powered by Gusto Embedded. This view covers the EMPLOYER setup
// journey: provision → embedded Gusto Flows onboarding (iframe) → step
// checklist → "pending Gusto approval" (2-3 business days) → ready.
// ALL Gusto calls are server-side (gustoProvisionCompany /
// gustoCreateOnboardingFlow / gustoGetOnboardingStatus callables — every one
// isTenantAdmin-gated). No tokens or company UUIDs ever reach this file; the
// flow URL is a short-lived capability fetched fresh per open, never persisted.
// See docs/dd-payroll-build-plan.md §850-B.
// TASK-1211: embedded setup is server-kill-switched while Gusto only grants DD
// a SANDBOX key — non-provisioned tenants see a Payroll-Prep value prop +
// Gusto referral link instead of the provision CTA, and sandbox-provisioned
// tenants get a "test setup — not live payroll" state with no resume button.

import {
  functions,
  httpsCallable,
  db, doc, getDoc, setDoc,
} from '../firebase-config.js?v=1868';

// Friendly display copy per Gusto onboarding step id (fallback = Gusto title).
const STEP_HINTS = {
  add_addresses: 'Confirm your company mailing, filing, and work addresses.',
  federal_tax_setup: 'Enter your EIN and federal tax details.',
  select_industry: 'Pick the industry classification for tax purposes.',
  add_bank_info: 'Link the bank account payroll is paid from.',
  verify_bank_info: 'Confirm the test deposits to verify your bank account.',
  add_employees: 'Add at least one employee to run payroll for.',
  state_setup: 'Enter your state tax account details.',
  payroll_schedule: 'Choose how often your team gets paid.',
  sign_all_forms: 'Sign the authorization forms as company signatory.',
  external_payroll: 'Report payrolls already run this year elsewhere.',
};

let _statusCache = null; // last gustoGetOnboardingStatus result (session only)

export function initPayroll(container, tenantId) {
  const isDemo = window.AppState?.isDemo || tenantId === 'demo';

  container.innerHTML = `<div class="payroll-view" id="payroll-view-root" style="max-width:980px;margin:0 auto;"></div>`;
  const root = document.getElementById('payroll-view-root');

  if (!isPayrollAdmin()) {
    root.innerHTML = shellCard(`
      <div class="empty-state">
        <div class="empty-state-icon">🔒</div>
        <div class="empty-state-title">Workforce access required</div>
        <div class="empty-state-text">Payroll Prep shows everyone's hours and pay, so it opens for the account owner or a login with the Workforce module. Ask the account owner to add Workforce to your access.</div>
      </div>`);
    return;
  }

  // TASK-1169 — the tab is now a two-section surface:
  //   1. Payroll Prep (always, for payroll admins — incl. demo): pay-period
  //      rollup + exceptions + Gusto-bound CSV/copy-paste export.
  //   2. Embedded-payroll section (TASK-850B): setup hero for un-provisioned
  //      tenants, onboarding status for provisioned ones — UNCHANGED so
  //      existing provisioned tenants keep their checklist (COD Independent
  //      Reviewer: don't break provisioned-tenant UI, no blank tab for others).
  root.innerHTML = `
    <div id="payroll-prep-root"></div>
    <div id="payroll-embedded-root" style="margin-top:16px;"></div>`;

  _prep.tenantId = tenantId;
  _prep.offset = -1; // default to the last COMPLETED period (the one being paid)
  _prep.data = null;
  _prep.error = null;
  _prep.expanded = {};
  _prep.ratesVisible = false; // 🔴 COD mitigation 1 — rates masked by default
  _prep.paychex = null; // TASK-9804 — tenants/{tid}/settings/paychex, loaded below
  _prep.paychexError = null;
  loadPrepData();
  _prepLoadPaychex();

  const embedRoot = document.getElementById('payroll-embedded-root');
  if (isDemo) {
    embedRoot.innerHTML = shellCard(`
      <div class="empty-state">
        <div class="empty-state-icon">💵</div>
        <div class="empty-state-title">Payroll provider setup isn't available in the demo</div>
        <div class="empty-state-text">DD Payroll connects to a live payroll provider, so setup only runs on a real account. The Payroll Prep screen above works with the demo's clock data.</div>
      </div>`);
    return;
  }
  const payroll = window.AppState?.tenantData?.payroll || {};
  if (!payroll.enabled || payroll.provider !== 'gusto') {
    renderSetupHero(embedRoot);
  } else if (!_isTenantOwnerById()) {
    // TASK-9804 — the gusto* status/onboarding callables stay owner/admin-only
    // server-side (they touch the provider connection). Payroll Prep now opens
    // for workforce holders too, so they get a calm card and NO call — this
    // widens nothing on the Gusto side.
    embedRoot.innerHTML = shellCard(`
      <div style="padding:12px 16px;font-size:0.88rem;color:var(--text-secondary);">
        Your company's Gusto connection is managed by the account owner. Payroll Prep above works the same for you.
      </div>`);
  } else {
    renderStatus(embedRoot);
  }
}

// Payroll Prep gate (TASK-9804 follow-up, Chris 9/24 "access = modules, never
// roles"): the SAME predicate as the server gate on recordPayrollRun /
// getPayrollRuns rows (_requireWorkforceModuleOrOwner in functions/src/index.js):
// the tenant OWNER by tenants.ownerId, or an active member holding the
// `workforce` module who is not view/upload-only. The member ROLE is never read.
// Demo keeps Prep (shared demo tenant, no member docs). Driver/public sessions
// never. No member doc and not the owner = refused (the server refuses too).
// Every payroll callable still enforces its own gate server-side.
function isPayrollAdmin() {
  const A = window.AppState || {};
  if (A.driverModePin || A.driverMode || A.isPublicAccess) return false;
  if (A.isDemo || A.tenant === 'demo') return true;
  const uid = A.user?.uid || null;
  const ownerId = A.tenantData?.ownerId || null;
  if (uid && ownerId && uid === ownerId) return true;
  const ma = A.memberAccess;
  if (!ma) return false;
  // Statuses the server binder refuses (suspended/removed, readOnly) + the app's own.
  if (['revoked', 'inactive', 'pending', 'suspended', 'removed'].includes(ma.status)) return false;
  if (ma.viewUploadOnly === true || ma.readOnly === true) return false;
  return Array.isArray(ma.modules) && ma.modules.includes('workforce');
}

// TASK-9804 — owner by tenants.ownerId (identity arm; never a role).
function _isTenantOwnerById() {
  const A = window.AppState || {};
  const uid = A.user?.uid || null;
  const ownerId = A.tenantData?.ownerId || null;
  return !!(uid && ownerId && uid === ownerId);
}

function shellCard(inner) {
  return `
    <div class="card">
      <div class="card-header" style="display:flex;align-items:center;gap:8px;">
        <span class="material-icons" style="color:var(--brand-blue);">payments</span> Payroll
      </div>
      ${inner}
    </div>`;
}

// ---------------------------------------------------------------------------
// State 1 — not provisioned. TASK-1211: the embedded "Set up payroll" hero is
// KILLED — Gusto declined DD's production key (2026-07-07), so the old CTA put
// every real owner/admin one click from entering EIN + bank details into a
// SANDBOX that will never run payroll. Until a production key lands, this card
// sells what actually works today: Payroll Prep (rendered above) + export to
// the tenant's own payroll provider. Copy stays "prep/export" — DD is NOT
// payroll-of-record (COD Domain: UDAP misrepresentation risk). The real
// boundary is server-side (gustoProvisionCompany / gustoCreateOnboardingFlow
// refuse in sandbox mode); this is just the honest UI on top of it.
// ---------------------------------------------------------------------------

// Outbound referral link for tenants with no payroll provider yet. Plain href —
// NEVER append tenant/user identifiers (COD Supply-Chain: an affiliate click-id
// must not leak tenantId/email). Swap for the tracked partner URL when Gusto
// issues one; the visible "(referral link)" FTC disclosure stays either way.
const GUSTO_REFERRAL_URL = 'https://gusto.com';

function gustoReferralHtml() {
  return `Don't have a payroll provider? We recommend
    <a href="${GUSTO_REFERRAL_URL}" target="_blank" rel="noopener noreferrer" style="color:var(--brand-blue);font-weight:600;">Gusto&nbsp;→</a>
    <span style="font-size:0.78rem;">(referral link)</span>`;
}

function renderSetupHero(root) {
  root.innerHTML = shellCard(`
    <div style="padding:24px 16px;text-align:center;">
      <div style="font-size:2.4rem;margin-bottom:8px;">🧾</div>
      <h3 style="margin:0 0 8px;color:var(--text-primary);">Pay-period prep, done for you</h3>
      <p style="max-width:520px;margin:0 auto 8px;color:var(--text-secondary);font-size:0.92rem;">
        DispatchDeck preps your pay period from the hours already on your clock —
        review it in <strong>Payroll Prep</strong> above, then export to your payroll provider.
      </p>
      <ul style="max-width:420px;margin:0 auto 16px;padding:0;list-style:none;text-align:left;color:var(--text-secondary);font-size:0.88rem;">
        <li style="padding:4px 0;"><span class="material-icons" style="font-size:16px;color:#059669;vertical-align:-3px;">check_circle</span> Regular + overtime hours rolled up per employee</li>
        <li style="padding:4px 0;"><span class="material-icons" style="font-size:16px;color:#059669;vertical-align:-3px;">check_circle</span> Exceptions caught before you pay — missing clock-outs, flagged shifts</li>
        <li style="padding:4px 0;"><span class="material-icons" style="font-size:16px;color:#059669;vertical-align:-3px;">check_circle</span> One-click export your provider can import</li>
      </ul>
      <div style="font-size:0.85rem;color:var(--text-secondary);">${gustoReferralHtml()}</div>
    </div>`);
}

// ---------------------------------------------------------------------------
// State 2/3/4 — provisioned: checklist, pending approval, or ready
// ---------------------------------------------------------------------------
async function renderStatus(root) {
  root.innerHTML = shellCard(`
    <div style="padding:32px;text-align:center;color:var(--text-secondary);">
      <span class="material-icons" style="animation:spin 1s linear infinite;display:inline-block;">progress_activity</span>
      <div style="margin-top:8px;font-size:0.88rem;">Checking payroll setup status…</div>
    </div>`);

  let status;
  try {
    const getStatus = httpsCallable(functions, 'gustoGetOnboardingStatus');
    status = (await getStatus({})).data;
    _statusCache = status;
  } catch (err) {
    console.error('[Payroll] status fetch failed:', err);
    root.innerHTML = shellCard(`
      <div class="empty-state">
        <div class="empty-state-icon">⚠️</div>
        <div class="empty-state-title">Couldn't load payroll status</div>
        <div class="empty-state-text">${escapeHtml(err?.message || 'Please try again in a moment.')}</div>
        <button class="btn btn-secondary" id="payroll-retry-btn" style="margin-top:12px;">Retry</button>
      </div>`);
    document.getElementById('payroll-retry-btn')?.addEventListener('click', () => renderStatus(root));
    return;
  }

  const { onboardingCompleted, approved, steps, stepsCompleted, stepsTotal, sandbox } = status;

  if (sandbox) {
    // TASK-1211 — this tenant was provisioned into the payroll provider's TEST
    // environment (trippin-trucking, during the 850-A/B build). Banner only —
    // no Continue button (the server refuses flow-minting in sandbox mode
    // anyway) and no checklist (the server short-circuits before Gusto, so
    // there are no live steps to show); say WHY instead of a dead CTA.
    root.innerHTML = shellCard(`
      <div style="display:flex;gap:10px;align-items:flex-start;background:rgba(217,119,6,0.08);border:1px solid rgba(217,119,6,0.35);border-radius:10px;padding:12px 14px;margin:8px 4px;">
        <span class="material-icons" style="color:#D97706;">science</span>
        <div>
          <div style="font-weight:600;color:var(--text-primary);">Test setup — not live payroll</div>
          <div style="font-size:0.85rem;color:var(--text-secondary);">This company was connected to the payroll provider's <strong>test environment</strong> while embedded payroll was being built — it can't run real payroll, and setup can't continue until DispatchDeck's production approval lands. Use <strong>Payroll Prep</strong> above to export this period's hours to the payroll provider you actually use. ${gustoReferralHtml()}</div>
        </div>
      </div>`);
    return;
  }

  let stateBanner = '';
  let actionRow = '';
  if (approved) {
    stateBanner = `
      <div style="display:flex;gap:10px;align-items:flex-start;background:rgba(5,150,105,0.08);border:1px solid rgba(5,150,105,0.35);border-radius:10px;padding:12px 14px;margin-bottom:16px;">
        <span class="material-icons" style="color:#059669;">verified</span>
        <div>
          <div style="font-weight:600;color:var(--text-primary);">Payroll is ready</div>
          <div style="font-size:0.85rem;color:var(--text-secondary);">Your company is approved to run payroll. Running your first payroll from DispatchDeck is coming next — hang tight.</div>
        </div>
      </div>`;
  } else if (onboardingCompleted) {
    stateBanner = `
      <div style="display:flex;gap:10px;align-items:flex-start;background:rgba(217,119,6,0.08);border:1px solid rgba(217,119,6,0.35);border-radius:10px;padding:12px 14px;margin-bottom:16px;">
        <span class="material-icons" style="color:#D97706;">hourglass_top</span>
        <div>
          <div style="font-weight:600;color:var(--text-primary);">Setup submitted — pending approval</div>
          <div style="font-size:0.85rem;color:var(--text-secondary);">Your payroll provider is reviewing your company. This usually takes 2–3 business days. We'll unlock payroll runs as soon as it's approved — nothing else for you to do.</div>
        </div>
      </div>`;
  } else {
    actionRow = `
      <button id="payroll-continue-btn" class="btn btn-primary">
        <span class="material-icons" style="font-size:18px;">arrow_forward</span> Continue setup
      </button>`;
  }

  const pct = stepsTotal > 0 ? Math.round((stepsCompleted / stepsTotal) * 100) : 0;
  const stepRows = (steps || []).filter(s => s.required).map(s => `
    <div style="display:flex;align-items:flex-start;gap:10px;padding:10px 0;border-bottom:1px solid var(--border-color,rgba(148,163,184,0.2));">
      <span class="material-icons" style="font-size:20px;color:${s.completed ? '#059669' : 'var(--text-muted)'};">${s.completed ? 'check_circle' : 'radio_button_unchecked'}</span>
      <div style="flex:1;min-width:0;">
        <div style="font-weight:${s.completed ? '400' : '600'};color:var(--text-primary);font-size:0.9rem;">${escapeHtml(s.title)}</div>
        ${!s.completed && STEP_HINTS[s.id] ? `<div style="font-size:0.8rem;color:var(--text-secondary);">${STEP_HINTS[s.id]}</div>` : ''}
      </div>
    </div>`).join('');

  root.innerHTML = shellCard(`
    <div style="padding:8px 4px;">
      ${stateBanner}
      <div style="display:flex;align-items:center;justify-content:space-between;gap:12px;flex-wrap:wrap;margin-bottom:8px;">
        <div>
          <div style="font-weight:600;color:var(--text-primary);">Company setup</div>
          <div style="font-size:0.82rem;color:var(--text-secondary);">${stepsCompleted} of ${stepsTotal} steps complete</div>
        </div>
        <div style="display:flex;gap:8px;align-items:center;">
          ${actionRow}
          <button id="payroll-refresh-btn" class="btn btn-secondary" title="Refresh status">
            <span class="material-icons" style="font-size:18px;">refresh</span>
          </button>
        </div>
      </div>
      <div style="height:8px;background:var(--border-color,rgba(148,163,184,0.25));border-radius:4px;overflow:hidden;margin-bottom:12px;">
        <div style="height:100%;width:${pct}%;background:var(--brand-blue);border-radius:4px;transition:width .3s;"></div>
      </div>
      ${stepRows}
      <div style="margin-top:14px;font-size:0.76rem;color:var(--text-muted);">
        Setup screens are provided by our payroll partner and open securely inside DispatchDeck.
        Bank and tax details go directly to the payroll provider — DispatchDeck never stores them.
      </div>
    </div>`);

  document.getElementById('payroll-refresh-btn')?.addEventListener('click', () => renderStatus(root));
  document.getElementById('payroll-continue-btn')?.addEventListener('click', () => openOnboardingFlow(root));
}

// ---------------------------------------------------------------------------
// Embedded Gusto Flows iframe — fetched fresh per open (short-lived URL),
// fullscreen overlay, 100vh/100dvh (mobile-safe), never persisted.
// ---------------------------------------------------------------------------
async function openOnboardingFlow(root) {
  const btn = document.getElementById('payroll-continue-btn');
  if (btn) { btn.disabled = true; }
  let url;
  try {
    const createFlow = httpsCallable(functions, 'gustoCreateOnboardingFlow');
    url = (await createFlow({})).data?.url;
    if (!url) throw new Error('No setup link returned');
  } catch (err) {
    console.error('[Payroll] flow mint failed:', err);
    window.showToast?.(err?.message || 'Could not open payroll setup', 'error');
    if (btn) btn.disabled = false;
    return;
  }
  if (btn) btn.disabled = false;

  // Remove any prior overlay (belt + suspenders — closed overlays remove themselves).
  document.getElementById('payroll-flow-overlay')?.remove();

  const overlay = document.createElement('div');
  overlay.id = 'payroll-flow-overlay';
  overlay.style.cssText = 'position:fixed;inset:0;z-index:2000;background:var(--bg-primary,#0F172A);display:flex;flex-direction:column;height:100vh;height:100dvh;';
  overlay.innerHTML = `
    <div style="display:flex;align-items:center;justify-content:space-between;gap:12px;padding:10px 14px;background:var(--brand-navy,#0F172A);color:#fff;flex-shrink:0;">
      <div style="display:flex;align-items:center;gap:8px;font-weight:600;">
        <span class="material-icons" style="font-size:20px;">payments</span> Payroll setup
      </div>
      <button id="payroll-flow-close" class="btn btn-secondary btn-sm" style="flex-shrink:0;">
        <span class="material-icons" style="font-size:16px;">close</span> Done
      </button>
    </div>
    <iframe id="payroll-flow-iframe" title="Payroll setup"
      style="flex:1;width:100%;border:0;background:#fff;"
      allow="camera; clipboard-write; payment"></iframe>`;
  document.body.appendChild(overlay);
  document.getElementById('payroll-flow-iframe').src = url;

  document.getElementById('payroll-flow-close').addEventListener('click', () => {
    overlay.remove();
    // Re-pull status so the checklist reflects whatever they finished in Gusto.
    if (root && document.getElementById('payroll-view-root')) renderStatus(root);
  });
}

// ===========================================================================
// TASK-1169 — PAYROLL PREP (Workforce → Payroll Prep)
// ===========================================================================
// Pay-period prep screen feeding an EXTERNAL standalone Gusto via CSV /
// copy-paste. NO Gusto API/OAuth — pure DD-side, generic for every tenant.
// ALL numbers come from window.ClockModule.buildPayrollPrepData (clock.js),
// which routes OT through the single-source-of-truth computeCrossPeriodOT /
// isPayrollExempt helpers (re-derivation forbidden — 83h OT bug) and per-diem
// through the TASK-939 CSV-authoritative auto-calc. This file only renders
// and serializes.
//
// GUSTO IMPORT FORMAT (verified at build time, 2026-07-16 — Gusto Help Center
// "Run payroll with Smart Import", support.gusto.com article 999914471000000):
// Gusto's CURRENT hours-import is "Smart Import" inside Run payroll → "Import
// payroll data". There is NO rigid required template — Gusto auto-matches the
// uploaded spreadsheet's columns to its payroll fields (admin can remap any
// column; "Unmatched" columns are skipped) and matches employee ROWS by name.
// Hours must be decimals (1h15m = 1.25); Gusto-side fields include Regular
// hours, Overtime hours, Double overtime, Bonus, and reimbursements. We emit
// unambiguous headers those matchers land on. Pay RATES are deliberately NOT
// exported — Gusto owns rates (and the prep UI masks them by default).
// Per diem is exported in the Reimbursement column, NEVER folded into hours
// (🔴 COD mitigation 2). That is ROUTING, not a tax ruling — how it is taxed
// is the tenant's call in their payroll provider (Chris 2026-09-28, TASK-1483).
//
// TASK-4627 (2026-08-31) — ONE `Reimbursement` column, not two.
// VERIFIED against Gusto's own OpenAPI spec (gusto-embedded-docs MCP,
// PUT /v1/companies/{company_id}/payrolls/{payroll_id}): a payroll's
// `fixed_compensation_types` is exactly
//   [Bonus, Commission, Paycheck Tips, Cash Tips, Correction Payment, Reimbursement]
// — a SINGLE `Reimbursement` type. We used to emit `Per diem reimbursement`
// AND `Other reimbursement`; only one of them could ever map, and the other
// reported "No match found" and was DROPPED with no error and no total
// mismatch shown. On TITS that is $50/period each for two people, live.
// So the CSV now carries per diem + other reimbursements SUMMED into one
// `Reimbursement` column, and the per-diem/other split stays visible on the
// Payroll Prep table (its own Per Diem and Reimb. columns) plus an explicit
// on-screen breakdown under the export.
// 🔴 DO NOT "restore" the two columns — re-splitting them re-creates the
// silent money loss. If Gusto ever gains a second reimbursement type, verify
// it in the spec above FIRST.
//
// Hours columns: the same spec's `hourly_compensations` are "regular,
// overtime, and double overtime hours" — there is NO standard Holiday hours
// field. Holiday BASE hours are already inside Regular hours; holiday PREMIUM
// cannot be exported at all and is surfaced as a visible warning instead of a
// header Gusto would silently ignore (TASK-4627 gap 2).
const GUSTO_CSV_HEADERS = [
  'First name', 'Last name', 'Regular hours', 'Overtime hours',
  'Bonus', 'Reimbursement',
];

const _prep = {
  tenantId: null,
  offset: -1,          // 0 = current (in-progress) period; -1 = last completed
  data: null,
  error: null,
  loading: false,
  ratesVisible: false, // masked by default (COD mitigation 1); eye toggle
  expanded: {},        // rowIndex -> bool (day drill-down)
  lock: null,          // TASK-9443 { own:{run,changes}, earlier:[{run,cur,changes}] } — soft pay-period lock
  lockSeq: 0,          // TASK-9443 stale-async guard
  lockLoading: false,  // TASK-9443 true until the earlier-period check settles — export waits for it (a carry must not be missed)
  paychex: null,       // TASK-9804 normalized Paychex settings (null = not loaded yet)
  paychexError: null,  // TASK-9804 load failure message (section says so, never silently hides)
};

async function loadPrepData() {
  _prep.loading = true;
  _prep.error = null;
  renderPrep();
  try {
    if (!window.ClockModule?.buildPayrollPrepData) throw new Error('Clock module not loaded');
    _prep.data = await window.ClockModule.buildPayrollPrepData(_prep.offset, _prep.tenantId);
    if (!_prep.data) throw new Error('No pay-period data (owner/admin access required)');
  } catch (err) {
    console.error('[PayrollPrep] load failed:', err);
    _prep.data = null;
    _prep.error = err?.message || 'Could not load pay-period data';
  }
  _prep.loading = false;
  _prep.lock = null;
  _prep.lockLoading = !!(_prep.data && window.PayrollRunLock);
  renderPrep();
  if (_prep.data) {
    loadLockState();
    // A slow or hung lookup must never hold payroll hostage (soft lock): after 15s give up on it and let the export go.
    const _lkData = _prep.data;
    setTimeout(() => {
      if (_prep.lockLoading && _prep.data === _lkData) {
        _prep.lockSeq++; _prep.lockLoading = false; _prep.lock = null;
        console.warn('[PayrollPrep] pay-period lock check timed out — export allowed without it');
        renderPrep();
      }
    }, 15000);
  } // TASK-9443 — async, never blocks or fails Prep
}

function prepNav(direction) {
  const next = _prep.offset + direction;
  if (next > 0) return; // no future periods
  _prep.offset = next;
  _prep.expanded = {};
  loadPrepData();
}

function prepRefresh() { loadPrepData(); _prepLoadPaychex(); }

function prepToggleRates() {
  _prep.ratesVisible = !_prep.ratesVisible;
  renderPrep();
}

function prepToggleRow(idx) {
  _prep.expanded[idx] = !_prep.expanded[idx];
  renderPrep();
}

function prepShowEmployee(idx) {
  const row = _prep.data?.rows?.[idx];
  if (!row) return;
  // Deep-link into the Workforce employee detail when the clock view is live
  // (the normal path — this tab lives inside it). Best-effort elsewhere.
  try { window.ClockModule?.showEmployeeDetail?.(row.pid); } catch (_) { /* standalone route — no-op */ }
}

function _prepFmtMoney(n) { return '$' + (Number(n) || 0).toFixed(2); }
function _prepFmtHrs(n) { return (Number(n) || 0).toFixed(2); }
// TASK-10034 — a YYYY-MM-DD calendar day → "Oct 20" (no zone shift).
function _prepFmtYmd(ymd) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(ymd || ''));
  if (!m) return String(ymd || '');
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}
function _prepFmtDate(ms, opts) {
  return new Date(ms).toLocaleDateString('en-US', opts || { month: 'short', day: 'numeric', year: 'numeric' });
}
function _prepFmtTime(ms) {
  return new Date(ms).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
}
function _prepYmd(ms) {
  const d = new Date(ms);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// Rows destined for Gusto — S4: exclude rows with nothing to import (Gusto's
// import has no use for an all-blank employee row). Salaried people export NO
// hours (Gusto pays salary automatically) but keep bonus/per-diem/reimb.
// A person with hours but no DD rate still exports (Gusto owns the rate);
// they're surfaced as a warning, never silently dropped from pay.
// TASK-4627 — per diem + other reimbursements are SUMMED into the single
// `Reimbursement` column Gusto actually has (see GUSTO_CSV_HEADERS).
// TASK-9178 — `r.workerType` here is the ROW field clock.js builds from
// window.WorkerClass.payrollRow(person) (the ONE rule, Tax-tab value first);
// it is never the raw person field.
// TASK-4633 — a person classified as a 1099 contractor is NOT a payroll
// employee and must never land in the employee import (that is a W-2 row with
// the wrong tax treatment, on top of the contractor payment they are also
// owed). They are excluded here and listed on screen as "pay through the
// contractor flow" — excluded, never invisible.
function buildGustoRows() {
  const data = _prep.data;
  if (!data) return [];
  const out = [];
  data.rows.forEach(r => {
    if (r.workerType === 'contractor') return; // TASK-4633 — paid via Gusto → Pay contractors
    const reg = r.isSalary ? 0 : (r.reg || 0);
    const ot = r.isSalary ? 0 : (r.ot || 0);
    // TASK-1483 — personal-vehicle pay, routed by the TENANT'S setting (DD
    // makes no tax call — Chris 2026-09-28). vehicleReimb → the one
    // Reimbursement field. vehicleTaxable is non-zero ONLY when the tenant
    // turned on "treat the part the miles don't cover as taxable wages" for a
    // flat daily amount → it rides the Bonus column (an unmatched custom header
    // would be DROPPED silently — see GUSTO_CSV_HEADERS). Both splits are shown
    // on screen under the export.
    const vehReimb = r.vehicleReimb || 0;
    const vehTaxable = r.vehicleTaxable || 0;
    const bonus = (r.bonus || 0) + vehTaxable;
    const perDiem = r.perDiemDollars || 0;
    const reimb = r.reimbursement || 0;
    if (reg <= 0.004 && ot <= 0.004 && bonus <= 0.004 && perDiem <= 0.004 && reimb <= 0.004 && vehReimb <= 0.004) return;
    const reimbTotal = perDiem + reimb + vehReimb; // TASK-4627 — Gusto has ONE Reimbursement field
    const parts = (r.name || '').trim().split(/\s+/);
    out.push([
      parts[0] || '',
      parts.length > 1 ? parts.slice(1).join(' ') : '',
      reg > 0 ? reg.toFixed(2) : '',
      ot > 0 ? ot.toFixed(2) : '',
      bonus > 0 ? bonus.toFixed(2) : '',
      reimbTotal > 0 ? reimbTotal.toFixed(2) : '',
    ]);
  });
  return out;
}

// TASK-4627 — the per-diem/other split the CSV can no longer carry, kept
// visible on screen so the combined `Reimbursement` figure is always
// reconcilable back to its two sources.
function _prepReimbSplitRows() {
  // TASK-1483 — vehicle reimbursement is a third source in the same field.
  return (_prep.data?.rows || [])
    .filter(r => r.workerType !== 'contractor'
      && [(r.perDiemDollars || 0), (r.reimbursement || 0), (r.vehicleReimb || 0)].filter(v => v > 0.004).length >= 2)
    .map(r => ({ name: r.name, perDiem: r.perDiemDollars || 0, other: r.reimbursement || 0, vehicle: r.vehicleReimb || 0,
                 total: (r.perDiemDollars || 0) + (r.reimbursement || 0) + (r.vehicleReimb || 0) }));
}

// TASK-1483 — a vehicle amount the tenant chose to pay as wages rides Bonus.
function _prepBonusSplitRows() {
  return (_prep.data?.rows || [])
    .filter(r => r.workerType !== 'contractor' && (r.vehicleTaxable || 0) > 0.004)
    .map(r => ({ name: r.name, bonus: r.bonus || 0, vehicle: r.vehicleTaxable, total: (r.bonus || 0) + r.vehicleTaxable }));
}

// Hard gate shared by both export paths — the disabled buttons are UX; this
// is the guarantee: unsettled hours (missing clock-outs) never reach Gusto.
function _prepExportBlocked() {
  // TASK-9443 — the earlier-period check is still running: exporting now would stamp the
  // period without carrying changes to earlier payrolls. A second or two, never a failure.
  if (_prep.lockLoading) {
    window.showToast?.('Still checking earlier pay periods for changes — try again in a moment', 'info');
    return true;
  }
  const crit = _prep.data?.exceptions?.criticalCount || 0;
  if (crit > 0) {
    window.showToast?.(`Export blocked — ${crit} missing clock-out${crit !== 1 ? 's' : ''} must be resolved first`, 'error');
    return true;
  }
  // TASK-9439 — "Approval before payroll" set to BLOCK: same hard gate as the
  // missing clock-outs (the disabled buttons are UX; this is the guarantee).
  const ap = _prepApprovalBlock();
  if (ap > 0) {
    window.showToast?.(`Export blocked — ${ap} item${ap !== 1 ? 's' : ''} awaiting approval (Workforce → Claims)`, 'error');
    return true;
  }
  return false;
}

// TASK-9439 — pending approval items that block the export (0 = not blocking:
// setting off, or nothing pending). The setting is the tenant's, audited.
function _prepApprovalBlock() {
  const a = _prep.data?.exceptions?.approval;
  return a && a.blocksExport ? (a.pending || []).length : 0;
}

// TASK-9439 — one item line with its "approve" link. pid/ymd ride data
// attributes (never an inline-onclick JS string — the TASK-9178 lesson);
// the delegated click handler in renderPrep opens Workforce → Claims.
function _prepApprovalItemHTML(x, label) {
  const amt = x.amount > 0.004 ? ` — ${_prepFmtMoney(x.amount)}` : '';
  const what = x.hoursOnly ? `${label}, hours day (no miles)` : `${label}${x.miles ? `, ${Number(x.miles).toFixed(1)} mi` : ''}`;
  // TASK-9451 — a shift on that day has no clock-out yet, and Claims will not
  // let anyone approve the day until it closes. Say so, so the office acts.
  // TASK-9459 — another shift that day is unreviewed/stale; the approved one does not pay the day alone.
  const held = x.dayHeld ? ' · <strong style="color:#B45309;">another shift that day needs review</strong> — approve the whole day' : '';
  const why = x.openShift ? ' · <strong style="color:#B45309;">clock-out still open</strong> — close the shift in Workforce → Hours, then approve' : '';
  // COD r1 (UX): no Review link while the shift is open — Claims can't approve it yet.
  if (x.openShift) return `<div style="padding:1px 0;">${escapeHtml(x.name)} · ${escapeHtml(x.ymd)} · ${what}${amt}${why}</div>`;
  return `<div style="padding:1px 0;">${escapeHtml(x.name)} · ${escapeHtml(x.ymd)} · ${what}${amt}${why}${held}
    <span role="button" tabindex="0" data-prep-claim-pid="${escapeHtml(String(x.pid || ''))}" data-prep-claim-ymd="${escapeHtml(String(x.ymd || ''))}" style="margin-left:6px;color:var(--brand-blue);font-weight:600;cursor:pointer;text-decoration:underline;">Review →</span></div>`;
}

function prepOpenClaim(pid, ymd) {
  if (!window.ClockModule?.openClaimsFor) {
    window.showToast?.('Open Workforce → Claims to approve it.', 'error');
    return;
  }
  window.ClockModule.openClaimsFor(pid, ymd);
}

// ─────────────────────────────────────────────────────────────────────────
// TASK-9443 — PAY-PERIOD LOCK (SOFT; Chris 2026-09-29). Nothing blocks the
// office: exporting STAMPS the period (server-side, with a snapshot of exactly
// what was exported), and this screen then lists every hour that moved since.
// Drift = today's recomputed period vs the stored snapshot (window.PayrollRunLock
// .diffRows), so it is found whichever writer changed the hours. A change to an
// earlier exported period is shown here (the NEXT Prep) and is "carried" — and
// the earlier period's baseline moved forward — when this period is exported.
// Every failure path is quiet: a missing lock never breaks Payroll Prep.
// ─────────────────────────────────────────────────────────────────────────
// What the Gusto file carries, per person. Contractors are out (TASK-4633) by the
// SAME row gate buildGustoRows uses; the module itself never decides W-2 vs 1099.
function _prepSnapshot(rows) {
  return window.PayrollRunLock.snapshotRows((rows || []).filter(r => r.workerType !== 'contractor'));
}

function _prepRunKey(startMs, endMs) { return `${_prepYmd(startMs)}_${_prepYmd(endMs)}`; }
// A run whose changes a later export KEPT (not marked handled) — server-written.
function _prepRunIsOpen(r) { return !!(r && Array.isArray(r.open) && r.open.length); }

// How far back a period can be recomputed (a year of weekly periods). Older, or a
// moved schedule, falls back to the SERVER-stored open list (never dropped).
const _PREP_LOOKBACK = 54;
// Recompute an EARLIER exported period with the SAME builder Prep uses (map the
// run's start back to a pay-period offset; null when the schedule moved).
async function _prepForRun(run) {
  const cm = window.ClockModule;
  if (!cm || !cm.getPayPeriodDates || !cm.buildPayrollPrepData) return null;
  for (let off = _prep.offset - 1; off >= _prep.offset - _PREP_LOOKBACK; off--) {
    const d = cm.getPayPeriodDates(off);
    if (!d || !d.start) return null;
    const s = d.start.getTime();
    if (s === run.startMs) return cm.buildPayrollPrepData(off, _prep.tenantId);
    if (s < run.startMs) return null;
  }
  return null;
}

async function loadLockState() {
  const seq = ++_prep.lockSeq;
  const PL = window.PayrollRunLock;
  const data = _prep.data;
  if (!PL || !data) { _prep.lockLoading = false; return; }
  _prep.lockLoading = true;
  try {
    const runs = (await PL.refresh(true)).filter(r => Array.isArray(r.rows)); // a rows-less run (non-payroll reader) can't be diffed
    if (seq !== _prep.lockSeq || _prep.data !== data) return;
    if (!runs.length) { _prep.lock = { own: null, earlier: [] }; _prep.lockLoading = false; renderPrep(); return; }
    const ownRun = PL.findRunByKey(runs, _prepRunKey(data.startMs, data.endMs));
    const own = ownRun ? { run: ownRun, changes: PL.diffRows(ownRun.rows, _prepSnapshot(data.rows)) } : null;
    const earlier = [];
    // The 3 most recent earlier exports (a carry moved their baseline, so only NEW
    // drift shows) PLUS every run with OPEN items, however old (re-review COD 🔴: a
    // kept correction must never age out). The server lists open runs regardless of age.
    const before = runs.filter(x => x.endMs < data.startMs);
    const pick = before.filter((x, i) => i < 3 || _prepRunIsOpen(x));
    for (const r of pick) {
      const prior = await _prepForRun(r);
      if (seq !== _prep.lockSeq || _prep.data !== data) return;
      if (prior) {
        const cur = _prepSnapshot(prior.rows);
        earlier.push({ run: r, cur, changes: PL.diffRows(r.rows, cur), recomputed: true, wasOpen: _prepRunIsOpen(r) });
      } else if (_prepRunIsOpen(r)) {
        // Can't be recomputed here (schedule moved / too far back): show what the
        // server recorded. Never dropped — it stays until someone marks it handled.
        earlier.push({ run: r, cur: null, changes: r.open, recomputed: false, wasOpen: true });
      }
    }
    _prep.lock = { own, earlier };
  } catch (e) {
    console.warn('[PayrollPrep] pay-period lock check skipped:', e?.message || e);
    if (seq === _prep.lockSeq && _prep.data === data) _prep.lock = null;
  }
  if (seq === _prep.lockSeq) _prep.lockLoading = false; // settled (success, empty or failed) — export may proceed
  if (seq === _prep.lockSeq && _prep.data === data) renderPrep();
}

function _prepLockList(changes) {
  const PL = window.PayrollRunLock;
  const shown = changes.slice(0, 30).map(c => `<div style="padding:1px 0;">${escapeHtml(PL.describe(c))}</div>`).join('');
  return shown + (changes.length > 30 ? `<div style="padding:1px 0;color:var(--text-secondary);">…and ${changes.length - 30} more</div>` : '');
}

function _prepLockBanner() {
  const L = _prep.lock;
  const PL = window.PayrollRunLock;
  if (!L || !PL) return '';
  const out = [];
  if (L.own) {
    const r = L.own.run;
    const when = r.lastExportedAtMs ? `${_prepFmtDate(r.lastExportedAtMs, { month: 'short', day: 'numeric' })} ${_prepFmtTime(r.lastExportedAtMs)}` : 'earlier';
    const by = r.lastExportedBy ? ` by ${escapeHtml(r.lastExportedBy)}` : '';
    // A re-export moves the baseline; the server kept what it changed (never silent).
    const reEx = (r.reExports || []).map(e => `<div style="margin-top:6px;font-size:0.8rem;"><strong>Re-exported ${_prepFmtDate(e.atMs, { month: 'short', day: 'numeric' })}${e.by ? ' by ' + escapeHtml(e.by) : ''}</strong> with different hours than the export before it${e.total > e.delta.length ? ` (${e.total} changes, first ${e.delta.length} shown)` : ''}:${_prepLockList(e.delta)}</div>`).join('') +
      (r.carried || []).filter(c => c.changes && c.changes.length).map(c => `<div style="margin-top:6px;font-size:0.8rem;"><strong>This export took over changes to ${escapeHtml(c.runKey)}${c.total > c.changes.length ? ` (${c.total} changes, first ${c.changes.length} shown)` : ''}:</strong>${_prepLockList(c.changes)}</div>`).join('');
    if (L.own.changes.length) {
      out.push(`<div id="payroll-lock-own" style="margin:0 0 10px;padding:10px 12px;border-radius:8px;background:#FEF3C7;border:1px solid #F59E0B;color:#78350F;font-size:0.85rem;">
        <strong>Changed after payroll.</strong> This period was exported ${when}${by}. These hours moved since — they were already exported, so they need a correction in your payroll provider:
        <div style="margin-top:6px;">${_prepLockList(L.own.changes)}</div>${reEx}
        <div style="margin-top:6px;font-size:0.78rem;">Compared with what this screen showed at export, so a change to pay settings or worker type can also appear here. Exporting again records the current hours as the new baseline (and keeps this change on file).</div></div>`);
    } else {
      out.push(`<div id="payroll-lock-own" style="margin:0 0 10px;padding:8px 12px;border-radius:8px;background:var(--bg-secondary);border:1px solid var(--border-color);color:var(--text-secondary);font-size:0.82rem;">
        Payroll for this period was exported ${when}${by}. No hours have changed since.${reEx}</div>`);
    }
  }
  (L.earlier || []).filter(x => x.changes.length).forEach(x => {
    out.push(`<div class="payroll-lock-earlier" style="margin:0 0 10px;padding:10px 12px;border-radius:8px;background:#FEF3C7;border:1px solid #F59E0B;color:#78350F;font-size:0.85rem;">
      <strong>Changed after payroll — ${escapeHtml(x.run.startYmd)} to ${escapeHtml(x.run.endYmd)}.</strong> Already exported; these hours moved afterwards. Pay the difference in this run if it is owed:
      <div style="margin-top:6px;">${_prepLockList(x.changes)}</div>
      ${x.wasOpen && x.run.openSinceMs ? `<div style="margin-top:6px;font-size:0.78rem;">Kept open since ${_prepFmtDate(x.run.openSinceMs, { month: 'short', day: 'numeric', year: 'numeric' })}${x.run.openRecordedBy ? ' (last kept by ' + escapeHtml(x.run.openRecordedBy) + ')' : ''}.</div>` : ''}
      ${x.recomputed ? '' : '<div style="margin-top:6px;font-size:0.78rem;">This period can no longer be recomputed here, so this is the list as it was recorded.</div>'}
      <div style="margin-top:6px;font-size:0.78rem;">When you export this period you will be asked whether these were paid. They stay listed here until you say they were handled.</div></div>`);
  });
  return out.join('');
}

// Stamp the export (server time + actor + snapshot of exactly what was
// exported) and carry earlier periods' drift. Called only AFTER the file was
// delivered; a failure here never undoes or blocks the export.
// Everything the stamp needs, read SYNCHRONOUSLY when the file is produced — the
// clipboard path awaits, and a period change during that await must not re-point
// the stamp at another period or snapshot (COD Code Reviewer).
function _prepCaptureRun() {
  const d = _prep.data;
  if (!d || !window.PayrollRunLock) return null;
  return {
    d,
    tenantId: _prep.tenantId || window.AppState?.tenant,
    rows: _prepSnapshot(d.rows),
    // Every earlier run Prep looked at that has changes, or was open. `rows` = the
    // period recomputed now (null when it can't be). The SERVER computes every
    // change list from its stored baseline; these only say what to compare with.
    earlier: (_prep.lock?.earlier || []).filter(x => x.changes.length || x.wasOpen)
      .map(x => ({ runKey: x.run.key, rows: x.recomputed ? x.cur : null, n: x.changes.length, wasOpen: !!x.wasOpen })),
    carriedCount: (_prep.lock?.earlier || []).filter(x => x.changes.length).reduce((s, x) => s + x.changes.length, 0), // for the ack prompt only
  };
}

// carried = marked handled (baseline moves; a run that can't be recomputed applies
// its server-stored open list). kept = NOT handled: recorded open on that run, so it
// is listed on every later Prep however old it gets. An open run whose hours went
// back to what was exported (n = 0) is sent as kept with rows so the server closes it.
function _prepSplitEarlier(items, handled) {
  const carried = [], kept = [];
  (items || []).forEach(it => {
    if (handled && it.n > 0) carried.push(it.rows ? { runKey: it.runKey, rows: it.rows } : { runKey: it.runKey, applyOpen: true });
    else kept.push(it.rows ? { runKey: it.runKey, rows: it.rows } : { runKey: it.runKey });
  });
  return { carried, kept };
}

// Integration COD (Code Reviewer + Domain 🔴): the Gusto file carries ONLY this
// period's hours, so earlier-period drift must never be marked handled just because
// this period was exported — an unpaid correction would drop off the list. Ask, after
// the file is out (the export itself is never blocked). Yes = carry (baseline moves,
// the change list stays on this run). No / no dialog / error = carry nothing, so the
// changes keep showing on the next Prep.
async function _prepAckCarry(cap) {
  if (!cap) return cap;
  const items = Array.isArray(cap.earlier) ? cap.earlier : [];
  if (!items.some(it => it.n > 0)) return { ...cap, ..._prepSplitEarlier(items, false) }; // only closers, no question
  const n = cap.carriedCount || 0; // frozen with the file, like the rest of the capture
  let ok = false;
  try {
    ok = typeof window.showConfirm === 'function' && await window.showConfirm(
      'Earlier pay periods changed',
      `${n || 'Some'} hour change${n === 1 ? '' : 's'} in earlier, already-exported periods ${n === 1 ? 'is' : 'are'} not in this file. Mark ${n === 1 ? 'it' : 'them'} as handled only if the difference was paid (in this run or separately). Otherwise ${n === 1 ? 'it stays' : 'they stay'} listed here.`,
      { confirmText: 'Mark as handled', cancelText: 'Keep listing them' });
  } catch (_) { ok = false; }
  // ok = carried (handled); anything else = kept OPEN on its run (never dropped).
  return { ...cap, ..._prepSplitEarlier(items, ok === true) };
}

// TASK-9443 slice 2 — can THIS login stamp the run? Mirrors the server gate on
// recordPayrollRun (owner by tenants.ownerId, or the `workforce` module, not
// view/upload-only — modules, never roles). Payroll Prep itself opens for role
// owner/admin, so an ADMIN WITHOUT `workforce` can export but the stamp is
// refused. Say so up front and name the fix, instead of a vague toast after.
// No member doc (legacy/owner session) = let the server decide.
function _prepCanRecordRun() {
  const AS = window.AppState || {};
  const uid = AS.user?.uid || null;
  const ownerId = AS.tenantData?.ownerId || null;
  if (ownerId && uid && ownerId === uid) return true;
  const ma = AS.memberAccess;
  if (!ma) return true;
  if (ma.viewUploadOnly === true) return false;
  return Array.isArray(ma.modules) && ma.modules.includes('workforce');
}
const _PREP_NOT_RECORDED_MSG = 'Exported, but not recorded as a payroll run: your login does not have Workforce access. Hour changes made after this export will not be listed for this period. Ask the account owner to add Workforce to your access, then export again.';
function _prepNoteNotRecorded(kind, cap, why) {
  try {
    window.logAudit?.('payroll', 'PAYROLL_EXPORT_NOT_RECORDED', {
      source: 'payroll-prep-' + kind,
      period: cap && cap.d ? `${_prepYmd(cap.d.startMs)}_${_prepYmd(cap.d.endMs)}` : null,
      reason: String(why || '').slice(0, 120),
    });
  } catch (_) { /* best-effort */ }
}

async function _prepRecordRun(kind, cap) {
  if (!cap) return;
  if (!_prepCanRecordRun()) {
    // The server would refuse the stamp; the file is already out. Never ask the
    // carry question for a stamp that cannot land (it would record nothing).
    _prepNoteNotRecorded(kind, cap, 'no workforce module');
    window.showToast?.(_PREP_NOT_RECORDED_MSG, 'warning');
    return;
  }
  cap = await _prepAckCarry(cap);
  const d = cap.d;
  try {
    const fn = httpsCallable(functions, 'recordPayrollRun');
    await fn({
      tenantId: cap.tenantId,
      startYmd: _prepYmd(d.startMs), endYmd: _prepYmd(d.endMs), startMs: d.startMs, endMs: d.endMs,
      kind, rows: cap.rows, carried: cap.carried || [], kept: cap.kept || [],
    });
    window.showToast?.('Payroll run recorded — hours changed afterwards will be listed here.', 'success');
    if (_prep.data === d) await loadLockState();
  } catch (e) {
    console.warn('[PayrollPrep] recordPayrollRun failed:', e?.message || e);
    if (/permission-denied/i.test(String(e?.code || '')) || /permission-denied/i.test(String(e?.message || ''))) {
      _prepNoteNotRecorded(kind, cap, 'permission-denied');
      window.showToast?.(_PREP_NOT_RECORDED_MSG, 'warning');
      return;
    }
    window.showToast?.('Exported, but this payroll run could not be recorded (connection or server problem). Later hour changes will not be tracked for this period. Export again to record it.', 'warning');
  }
}

function _prepAuditExport(kind, rowCount) {
  try {
    window.logAudit?.('payroll', 'PAYROLL_GUSTO_EXPORT', {
      source: 'payroll-prep-' + kind,
      period: `${_prepYmd(_prep.data.startMs)}_${_prepYmd(_prep.data.endMs)}`,
      exporter: window.AppState?.user?.email || 'unknown',
      rowCount,
      unreviewedFlaggedShifts: _prep.data?.exceptions?.unreviewedCount || 0, // TASK-9794 — exported with flags still open
    });
  } catch (e) { console.warn('[PayrollPrep] export audit skipped:', e?.message || e); }
}

async function prepDownloadCSV() {
  if (!_prep.data || _prepExportBlocked()) return;
  if (!(await _prepConfirmUnreviewed())) return; // TASK-9794 — warn only
  if (!_prep.data || _prepExportBlocked()) return; // state may have moved while the dialog was open
  const gustoRows = buildGustoRows();
  const _cap = _prepCaptureRun(); // TASK-9443 — frozen with the file
  if (!gustoRows.length) { window.showToast?.('Nothing to export for this period', 'error'); return; }
  const esc = window.ClockModule.escapeCSV; // TASK-453 formula-injection guard — REUSED, never reimplemented
  // Strictly tabular — no footnote rows (Smart Import would read one as an
  // unmatched employee). Notes live on screen instead.
  const lines = [GUSTO_CSV_HEADERS.join(',')];
  gustoRows.forEach(r => lines.push(r.map(esc).join(',')));
  const filename = `gusto-hours-${_prepYmd(_prep.data.startMs)}_${_prepYmd(_prep.data.endMs)}.csv`;
  window.ClockModule.downloadCSV(filename, lines.join('\n'));
  window.showToast?.('Gusto hours file downloaded', 'success');
  _prepAuditExport('csv', gustoRows.length);
  _prepRecordRun('csv', _cap); // TASK-9443 — stamp the period (never throws)
}

async function prepCopyTSV() {
  if (!_prep.data || _prepExportBlocked()) return;
  if (!(await _prepConfirmUnreviewed())) return; // TASK-9794 — warn only
  if (!_prep.data || _prepExportBlocked()) return;
  const gustoRows = buildGustoRows();
  const _cap = _prepCaptureRun(); // TASK-9443 — frozen BEFORE the clipboard await
  if (!gustoRows.length) { window.showToast?.('Nothing to copy for this period', 'error'); return; }
  // Formula guard applies to pasted cells too; ALSO strip embedded tabs/newlines
  // (2nd-COD nit) — a tab inside a name would shift every pasted column right.
  const esc = (v) => String(window.ClockModule.escapeCSV(v)).replace(/[\t\n\r]+/g, ' ');
  const tsv = [GUSTO_CSV_HEADERS.join('\t'), ...gustoRows.map(r => r.map(esc).join('\t'))].join('\n');
  try {
    await navigator.clipboard.writeText(tsv);
    window.showToast?.('Copied — paste into Gusto or a spreadsheet', 'success');
    _prepAuditExport('copy', gustoRows.length);
    await _prepRecordRun('copy', _cap); // TASK-9443 — stamp the period (never throws)
  } catch (err) {
    console.error('[PayrollPrep] clipboard failed:', err);
    window.showToast?.('Copy failed — use Download CSV instead', 'error');
  }
}

// ── TASK-9804 — Paychex Flex hours file (SPI) ───────────────────────────────
// Same prep rows, same blocks (_prepExportBlocked + the unreviewed-shift
// question) and the same run stamp as the Gusto file; the row arithmetic lives
// in public/src/paychexExport.js (pure, unit-tested against buildGustoRows).
// Settings: tenants/{tid}/settings/paychex — the existing settings/{id} rule
// (members read, tenant admin writes), so no rules change. Off by default.
const _paychexCache = {}; // tenantId -> normalized settings; hr.js reads it to decide whether to show the Worker ID box

async function loadPaychexSettings(tenantId, opts) {
  await _paychexUi(); // sets window.PaychexExport (lazy)
  const PX = window.PaychexExport;
  const tid = tenantId || window.AppState?.tenant;
  if (!PX || !tid) return null;
  if (!(opts && opts.fresh) && _paychexCache[tid]) return _paychexCache[tid];
  const snap = await getDoc(doc(db, 'tenants', tid, 'settings', 'paychex'));
  const s = PX.normalizeSettings(snap.exists() ? snap.data() : null);
  _paychexCache[tid] = s;
  return s;
}

async function _prepLoadPaychex() {
  const tid = _prep.tenantId;
  try {
    const s = await loadPaychexSettings(tid, { fresh: true });
    if (_prep.tenantId !== tid) return;
    _prep.paychex = s;
    _prep.paychexError = null;
  } catch (e) {
    console.warn('[PayrollPrep] Paychex settings load failed:', e?.message || e);
    _prep.paychex = null;
    _prep.paychexError = e?.code === 'permission-denied' ? 'Your login cannot read the Paychex settings.' : 'Paychex settings could not be loaded — Refresh to try again.';
  }
  renderPrep();
}

// The Paychex screen code + SPI builder are LAZY (views/payrollPaychex.js) —
// perf budget: non-driver screens load on demand. Until it lands the section
// renders nothing; _prepLoadPaychex loads it before the settings read.
let _pxUi = null;
function _paychexHost() {
  return {
    get prep() { return _prep; },
    render: () => renderPrep(),
    exportBlocked: () => _prepExportBlocked(),
    confirmUnreviewed: () => _prepConfirmUnreviewed(),
    captureRun: () => _prepCaptureRun(),
    recordRun: (kind, cap) => _prepRecordRun(kind, cap),
    ymd: _prepYmd, fmtHrs: _prepFmtHrs, escapeHtml,
    saveSettings: (tid, data) => setDoc(doc(db, 'tenants', tid, 'settings', 'paychex'), data),
    cacheSet: (tid, s) => { _paychexCache[tid] = s; },
  };
}
async function _paychexUi() {
  if (_pxUi) return _pxUi;
  const m = await __ddChunkWrap(import('./payrollPaychex.js?v=9804002'));
  _pxUi = m.createPaychexUi(_paychexHost());
  return _pxUi;
}
async function prepDownloadPaychex() { return (await _paychexUi()).download(); }
async function prepOpenPaychexSetup() { return (await _paychexUi()).openSetup(); }
function _prepPaychexSectionHTML(exportDisabled, exportDisabledWhy) {
  return _pxUi ? _pxUi.sectionHTML(exportDisabled, exportDisabledWhy) : '';
}

// TASK-9794 — Needs-review list (amber: advisory, export still works).
function _prepReviewListHTML(items) {
  const rows = items.map(x => {
    const when = _prepFmtDate(x.inMs, { weekday: 'short', month: 'short', day: 'numeric' });
    const span = x.outMs ? `${_prepFmtTime(x.inMs)} – ${_prepFmtTime(x.outMs)}` : _prepFmtTime(x.inMs);
    const why = (x.reasons || []).length ? x.reasons.map(r => escapeHtml(r)).join(' · ') : 'flagged for review';
    const busy = _prep.accepting && _prep.accepting[x.id];
    return `<div style="display:flex;align-items:center;gap:10px;flex-wrap:wrap;padding:6px 0;border-top:1px solid rgba(217,119,6,0.18);">
        <div style="flex:1;min-width:220px;">
          <div><span class="prep-exc-name" style="cursor:pointer;text-decoration:underline;font-weight:600;color:var(--text-primary);" data-prep-pid="${escapeHtml(String(x.pid))}">${escapeHtml(x.name)}</span>
            <span style="color:var(--text-secondary);"> · ${escapeHtml(when)} · ${escapeHtml(span)} · ${_prepFmtHrs(x.hours)}h${x.jobTitle ? ' · ' + escapeHtml(x.jobTitle) : ''}</span></div>
          <div style="font-size:0.8rem;color:#B45309;">${why}</div>
        </div>
        <button class="btn btn-secondary btn-sm" data-prep-accept="${escapeHtml(String(x.id))}" ${busy ? 'disabled' : ''} title="Mark this shift as reviewed. Hours stay exactly as they are; the acceptance is recorded with your login.">
          <span class="material-icons" style="font-size:16px;">task_alt</span> ${busy ? 'Accepting…' : 'Accept'}
        </button>
      </div>`;
  }).join('');
  return `
      <div style="display:flex;gap:10px;align-items:flex-start;background:rgba(217,119,6,0.08);border:1px solid rgba(217,119,6,0.35);border-radius:10px;padding:12px 14px;margin-bottom:10px;">
        <span class="material-icons" style="color:#D97706;">rate_review</span>
        <div style="flex:1;min-width:0;">
          <div style="font-weight:700;color:var(--text-primary);">Needs review — ${items.length} shift${items.length !== 1 ? 's' : ''}</div>
          <div style="font-size:0.85rem;color:var(--text-secondary);margin-bottom:4px;">Flagged by DispatchDeck (entered long after the fact, overlapping, in the future, over the shift limit, a Shop / no-job shift past midnight, or corrected by the driver). Hours are included in the export as-is. Open the employee to check the entry, then Accept — or fix it in Workforce → Hours and Refresh.</div>
          <div style="font-size:0.85rem;">${rows}</div>
        </div>
      </div>`;
}

// TASK-9794 — one-click Accept. The write + both audit rows live in clock.js
// (acceptClockEntryReview); this only drives the button and reloads Prep.
async function prepAcceptReview(entryId) {
  if (!entryId || !_prep.data) return;
  if (!window.ClockModule?.acceptClockEntryReview) { window.showToast?.('Clock module not loaded', 'error'); return; }
  _prep.accepting = _prep.accepting || {};
  if (_prep.accepting[entryId]) return;
  _prep.accepting[entryId] = true;
  renderPrep();
  try {
    // COD r2 — hand Accept exactly what this row showed; if the stored flags /
    // times differ now, it refuses (code 'review-changed') instead of
    // accepting a flag nobody saw.
    const shown = (_prep.data?.exceptions?.reviewItems || []).find(x => String(x.id) === String(entryId)) || null;
    if (!shown) throw Object.assign(new Error('This shift is no longer in the list'), { code: 'review-changed' });
    await window.ClockModule.acceptClockEntryReview(_prep.tenantId, entryId, shown);
    window.showToast?.('Shift accepted — it stays in payroll exactly as recorded', 'success');
  } catch (err) {
    console.error('[PayrollPrep] accept failed:', err);
    if (err?.code === 'review-changed') window.showToast?.('Flags changed on this shift since the list loaded — reloaded, review it again', 'warning');
    else window.showToast?.(err?.code === 'permission-denied' ? 'Only Workforce or admin logins can accept a flagged shift, and nobody can accept their own' : ('Could not accept: ' + (err?.message || err)), 'error');
  }
  delete _prep.accepting[entryId];
  await loadPrepData(); // re-derive flags/exceptions from the entries (never patch the local copy)
}

// TASK-9794 — WARN, never refuse (Chris q03): if flagged shifts are still
// unreviewed, say how many and ask before the file goes out.
async function _prepConfirmUnreviewed() {
  const n = _prep.data?.exceptions?.unreviewedCount || 0;
  if (!n) return true;
  if (typeof window.showConfirm !== 'function') return true; // never trap the export on a missing dialog
  return await window.showConfirm(
    `${n} flagged shift${n !== 1 ? 's' : ''} not reviewed`,
    `${n} shift${n !== 1 ? 's' : ''} in this period ${n !== 1 ? 'are' : 'is'} still marked "Needs review". The hours are included exactly as recorded. Export anyway? (You can Accept each one above first.)`,
    { confirmText: 'Export anyway', cancelText: 'Go back', icon: 'rate_review', iconColor: '#D97706' },
  );
}

function _prepExceptionsBanner(data) {
  const ex = data.exceptions || {};
  const parts = [];
  // TASK-9439 — approval before payroll. Pending items are never paid until
  // approved; with the tenant's "block" setting ON the export is disabled
  // (red, same shape as missing clock-outs), OFF it warns with the count.
  const _ap = ex.approval || { pending: [], denied: [] };
  const _apLabel = escapeHtml(_ap.typeLabel || window.VehicleReimb?.DEFAULT_PAY_TYPE_NAME || 'Vehicle Reimbursement');
  const _apPending = _ap.pending || [];
  const _apItems = _apPending.map(x => _prepApprovalItemHTML(x, _apLabel)).join('');
  if (_apPending.length && _ap.blocksExport) {
    parts.push(`
      <div style="display:flex;gap:10px;align-items:flex-start;background:rgba(220,38,38,0.08);border:1px solid rgba(220,38,38,0.45);border-radius:10px;padding:12px 14px;margin-bottom:10px;">
        <span class="material-icons" style="color:#DC2626;">error</span>
        <div style="flex:1;min-width:0;">
          <div style="font-weight:700;color:var(--text-primary);">Export disabled — ${_apPending.length} item${_apPending.length !== 1 ? 's' : ''} awaiting approval</div>
          <div style="font-size:0.85rem;color:var(--text-secondary);">Your approval setting blocks payroll until these are approved or denied. Approve each in Workforce → Claims (needs the Workforce module), then Refresh. Only the account owner can turn the block off (Workforce → Claims → Approval before payroll).${_apItems}</div>
        </div>
      </div>`);
  }
  if ((ex.missingClockOuts || []).length) {
    const names = ex.missingClockOuts.map(x => `<span class="prep-exc-name" style="cursor:pointer;text-decoration:underline;" data-prep-pid="${escapeHtml(String(x.pid))}">${escapeHtml(x.name)}</span>${x.count > 1 ? ` (×${x.count})` : ''}`).join(', ');
    parts.push(`
      <div style="display:flex;gap:10px;align-items:flex-start;background:rgba(220,38,38,0.08);border:1px solid rgba(220,38,38,0.45);border-radius:10px;padding:12px 14px;margin-bottom:10px;">
        <span class="material-icons" style="color:#DC2626;">error</span>
        <div style="flex:1;min-width:0;">
          <div style="font-weight:700;color:var(--text-primary);">Export disabled — ${ex.criticalCount} missing clock-out${ex.criticalCount !== 1 ? 's' : ''}</div>
          <div style="font-size:0.85rem;color:var(--text-secondary);">Still-open shifts in this period: ${names}. Close them in Workforce → Hours (open the employee, fix the entry), then Refresh.</div>
        </div>
      </div>`);
  }
  // TASK-4627 / TASK-4633 🔴 — MONEY (or a whole person) THAT THE FILE CANNOT
  // CARRY. This is its own red box, deliberately NOT folded into the amber
  // warnings: an amber "needs review" reads as advisory, and these are
  // "download this file and something is missing or wrong". It does not
  // BLOCK export — blocking payroll over one unclassified person traps
  // everybody — but it is impossible to miss and it says what to do.
  const notExported = [];
  if ((ex.holidayPremium || []).length) {
    const items = (ex.holidayPremium || []).map(x =>
      `<div style="padding:1px 0;">${escapeHtml(x.name)}: <strong>${_prepFmtHrs(x.premiumHours)} premium hours</strong> (on ${_prepFmtHrs(x.holidayHours)} holiday hours worked)</div>`).join('');
    notExported.push(`<div><strong>Holiday premium is NOT in this file — enter it in Gusto by hand.</strong>
      Gusto's import only accepts Regular, Overtime and Double-overtime hours, so there is nowhere for premium
      hours to go. The holiday hours themselves ARE included (they are inside Regular hours) — it is only the
      extra premium above regular pay that has to be added in Gusto.${items}</div>`);
  }
  if (ex.internalTimeUnavailable) {
    notExported.push(`<div><strong>Internal-time hours could not be checked</strong> — the customer list failed to load, so Shop/Yard/Office hours are not broken out here. Refresh before paying internal time at a separate rate.</div>`);
  }
  // TASK-9388 — the pay config (rates + "keeps normal rate" lists) could not be
  // read, so the breakdown below may list people who should keep their normal
  // rate, and no internal rate is priced. Never silent.
  if (ex.internalPayConfigUnavailable) {
    notExported.push(`<div><strong>Internal-time pay settings could not be loaded</strong> — the rates and the "keep their normal rate" lists from Pay Settings are NOT applied here. Ask the account owner (or a Workforce-module login) to run payroll, or refresh.</div>`);
  }
  // TASK-1483 — vehicle claims could not be priced for this tenant.
  if (ex.vehicleScopeUnknown) {
    notExported.push(`<div><strong>${_pvLabel()} claims could not be priced</strong> — this tenant's mileage rate isn't loaded here, so ${_pvLabel()} is NOT in this file. Open Payroll Prep from the tenant's own Workforce tab.</div>`);
  }
  // TASK-1483 🔴 — per diem AND vehicle pay for one person: the missed-migration
  // double-pay shape (hrPerDiem still holding "truck pay"). Red, not blocking.
  if ((ex.vehicleAndPerDiem || []).length) {
    const items = ex.vehicleAndPerDiem.map(x =>
      `<div style="padding:1px 0;"><span class="prep-exc-name" style="cursor:pointer;text-decoration:underline;" data-prep-pid="${escapeHtml(String(x.pid))}">${escapeHtml(x.name)}</span>: per diem ${_prepFmtMoney(x.perDiem)} + ${_pvLabel()} ${_prepFmtMoney(x.vehicle)}</div>`).join('');
    notExported.push(`<div><strong>Paid BOTH per diem and ${_pvLabel()} — confirm it is not the same money.</strong>
      If this person's per diem was really "truck pay", it is now paid twice: clear their Per Diem on HR → Employment info
      (vehicle pay replaces it). If they genuinely get both, ignore this.${items}</div>`);
  }
  if (ex.holidayScopeUnknown) {
    notExported.push(`<div><strong>Holiday premium could not be checked</strong> for this tenant (its holiday schedule isn't loaded), so this file may be missing premium hours. Verify holidays in Gusto by hand before you submit.</div>`);
  }
  if ((ex.unclassifiedWorkers || []).length) {
    const items = (ex.unclassifiedWorkers || []).map(x =>
      `<div style="padding:1px 0;"><span class="prep-exc-name" style="cursor:pointer;text-decoration:underline;" data-prep-pid="${escapeHtml(String(x.pid))}">${escapeHtml(x.name)}</span> — ${_prepFmtHrs(x.hours)}h worked, no pay rate on file</div>`).join('');
    notExported.push(`<div><strong>Worked, but nobody has said how they get paid.</strong>
      These people have hours and no pay rate, and no one has marked them an employee or a contractor.
      <strong>They WILL export below as regular W-2 employee rows.</strong> If any of them is actually a 1099
      contractor, that is a double payment with the wrong tax treatment — set <em>Worker Classification</em> to
      Contractor on their Employment info first (contractors are paid through Gusto → Pay → Pay contractors,
      never on a payroll run).${items}</div>`);
  }
  // TASK-9178 — a legacy record whose two classification fields disagree.
  if ((ex.classificationConflicts || []).length) {
    const items = (ex.classificationConflicts || []).map(x =>
      `<div style="padding:1px 0;"><span class="prep-exc-name" style="cursor:pointer;text-decoration:underline;" data-prep-pid="${escapeHtml(String(x.pid))}">${escapeHtml(x.name)}</span> — treated as ${escapeHtml(window.WorkerClass?.label?.(x.workerClass, 'unclassified') || 'unclassified')}</div>`).join('');
    notExported.push(`<div><strong>Classification conflict — confirm before paying.</strong>
      These people were marked W-2 in one place and 1099 in another (older records). The Tax tab
      classification is the one used here, so each is already in or out of this file on that basis.
      To clear it: HR → the person → Tax tab → Edit, pick the correct classification from the list (it opens on "— Confirm classification —"), then Save. Saving without picking leaves it unconfirmed.${items}</div>`);
  }
  if ((ex.contractorsExcluded || []).length) {
    const items = (ex.contractorsExcluded || []).map(x =>
      `<div style="padding:1px 0;">${escapeHtml(x.name)} — ${_prepFmtHrs(x.hours)}h${x.vehicle > 0.004 ? ` + $${Number(x.vehicle).toFixed(2)} ${_pvLabel()}` : ''}</div>`).join('');
    notExported.push(`<div><strong>Contractors are not in this file — pay them separately.</strong>
      Marked as contractors, so they are deliberately excluded from the employee import.
      Pay them in Gusto under <strong>Pay → Pay contractors</strong>; their hours (and any ${_pvLabel()}
      pay) are shown here so they don't get forgotten.${items}</div>`);
  }
  if (notExported.length) {
    parts.push(`
      <div style="display:flex;gap:10px;align-items:flex-start;background:rgba(220,38,38,0.08);border:1px solid rgba(220,38,38,0.45);border-radius:10px;padding:12px 14px;margin-bottom:10px;">
        <span class="material-icons" style="color:#DC2626;">report_problem</span>
        <div style="flex:1;min-width:0;">
          <div style="font-weight:700;color:var(--text-primary);margin-bottom:4px;">Not included in the export</div>
          <div style="font-size:0.85rem;color:var(--text-secondary);">${notExported.map(w => `<div style="padding:3px 0;">${w}</div>`).join('')}</div>
        </div>
      </div>`);
  }

  const warns = [];
  // TASK-4627 — name-match risk. Gusto matches rows by name; we split on the
  // first space, so a middle name or suffix produces a last name Gusto won't
  // find. Gusto says "no match found" on its side; DD never said it was
  // likely. Now it does, before the download.
  if ((ex.nameSplitRisk || []).length) {
    const items = (ex.nameSplitRisk || []).map(x =>
      `<div style="padding:1px 0;">${escapeHtml(x.name)} → exports as first name <strong>${escapeHtml(x.first)}</strong>, last name <strong>${escapeHtml(x.last)}</strong></div>`).join('');
    warns.push(`<strong>These names may not match in Gusto:</strong> Gusto matches each row by name, and DD
      treats everything after the first word as the last name. Check these rows matched after you upload — if
      Gusto says "no match found", pick the right person by hand (or rename them in DD to match Gusto exactly).${items}`);
  }
  // TASK-1558 — pay changed inside the displayed period: name + old → new @
  // timestamp. Dollar values honor the rate mask (COD mitigation 1) — the eye
  // toggle reveals them; timestamps and names always show so the change is
  // never silent.
  if ((ex.rateChanges || []).length) {
    const maskMoney = (s) => _prep.ratesVisible ? s : String(s).replace(/\$[\d,]+(\.\d+)?/g, '••••');
    const items = ex.rateChanges.map(x =>
      `<div style="padding:2px 0;"><span class="prep-exc-name" style="cursor:pointer;text-decoration:underline;" data-prep-pid="${escapeHtml(String(x.pid))}">${escapeHtml(x.name)}</span>: ${x.changes.map(c => escapeHtml(maskMoney(c))).join('; ')}${x.needsHandReview ? ` — <strong style="color:#DC2626;">review by hand</strong> (${x.notes.map(escapeHtml).join('; ')})` : ''}</div>`).join('');
    warns.push(`<strong>Pay changed mid-period:</strong> hours before the change pay the old rate, hours after pay the new rate (split at the exact time); a week with two rates gets its OT premium from the weighted-average regular rate (29 CFR 778.115). Expand each row for the split.${items}`);
  }
  // TASK-9387 — internal time (Shop / Yard / …) IS in the Gusto file, lumped
  // into Regular/Overtime: the import has no per-rate columns we can trust
  // (unmatched headers are dropped silently — see GUSTO_CSV_HEADERS). Amber,
  // not red: nothing is missing, and most tenants pay internal time at the
  // normal rate. Named so a tenant that pays it separately moves those hours.
  //
  // COD 2026-09-24 — three things kept structurally apart so nobody pays twice
  // or moves a guess:
  //   • "move" list = CONFIRMED customers only (Internal time only ticked),
  //     hours PAYABLE this period (what Regular/Overtime in this file pays),
  //     with hours worked shown beside it when they differ;
  //   • "already paid last period — do not move" = the earlier part of a
  //     workweek that ends in this period, its own block, never in a figure
  //     above;
  //   • "unconfirmed" = counted only because of the customer's name — never
  //     offered to move, only "tick Internal time only on the customer".
  const _itRows = ex.internalTime || [];
  // TASK-9388 — when Pay Settings holds a rate, show it (masked like every
  // other dollar figure) plus DD's estimated gross, so the Gusto run can be
  // checked against it.
  const _itPayBit = (p) => {
    if (!p) return '';
    const segs = (p.segments || []).map(s => `${escapeHtml(s.label)} @ ${_prep.ratesVisible ? _prepFmtMoney(s.rate) : '••••'}/hr`).join(', ');
    const gross = p.gross !== null && p.gross !== undefined ? ` — est. gross ${_prep.ratesVisible ? _prepFmtMoney(p.gross) : '••••'}` : (p.supported === false ? ' — <strong style="color:#DC2626;">review by hand</strong>' : '');
    return ` <span style="color:var(--text-secondary);">[${segs}${gross}]</span>`;
  };
  const _itMove = _itRows.filter(x => (x.items || []).some(i => i.hours > 0.004));
  if (_itMove.length) {
    const items = _itMove.map(x => {
      const parts = x.items.filter(i => i.hours > 0.004).map(i =>
        `<strong>${escapeHtml(i.label)} ${_prepFmtHrs(i.hours)}h payable</strong>${Math.abs((i.worked || 0) - i.hours) > 0.004 ? ` (${_prepFmtHrs(i.worked || 0)}h worked this period)` : ''}`).join(', ');
      return `<div style="padding:1px 0;">${escapeHtml(x.name)}: ${parts}${x.byWeek ? ` <span style="color:var(--text-secondary);">— by week: ${escapeHtml(x.byWeek)}</span>` : ''}${_itPayBit(x.pay)}</div>`;
    }).join('');
    warns.push(`<strong>Internal-time hours (included in Regular/Overtime):</strong> if you pay internal time at a different rate, move these PAYABLE hours to that pay rate in Gusto, week by week (overtime is figured per workweek). Payable can differ from worked when a workweek crosses the pay-period line. People on a customer's "keep their normal rate" list in Pay Settings are not listed.${items}`);
    // TASK-9388 — Option 1 (29 CFR 778.419) cannot ride the Gusto file: it
    // carries hours only and Gusto figures multi-rate OT with the weighted
    // average itself. Say so instead of implying the file pays Option 1.
    if (data.internalOtMethod === 'rate_of_work' && data.internalPayAny) {
      warns.push(`<strong>Overtime method is "rate of the work performed" (29 CFR 778.419):</strong> Gusto figures overtime on multiple rates with the weighted average itself, and this file carries hours only — so Gusto's overtime will not match DD's estimate. Enter each week's OT at the right rate in Gusto by hand (expand a row for the split). This method is only allowed with a written agreement made before the work.`);
    }
  }
  const _itPaid = _itRows.filter(x => x.alreadyPaid);
  if (_itPaid.length) {
    const items = _itPaid.map(x =>
      `<div style="padding:1px 0;">${escapeHtml(x.name)}: ${escapeHtml(x.alreadyPaid)}</div>`).join('');
    warns.push(`<strong>Already paid last period — do not move:</strong> internal hours from the earlier part of a workweek that ends in this period. They were paid on last period's run; they only matter for that week's weighted-average overtime rate. Moving them pays them twice.${items}`);
  }
  const _itUnconf = _itRows.filter(x => (x.unconfirmed || []).length);
  if (_itUnconf.length) {
    const items = _itUnconf.map(x =>
      `<div style="padding:1px 0;">${escapeHtml(x.name)}: ${x.unconfirmed.map(u => `${escapeHtml(u.label)} ${_prepFmtHrs(u.worked)}h`).join(', ')}</div>`).join('');
    warns.push(`<strong>Unconfirmed internal time:</strong> these customers look internal only because of their name — "Internal time only" is not ticked. Their hours stay in Regular/Overtime as normal. If they really are shop/yard time, tick <em>Internal time only</em> on the customer (CRM → customer → Edit → Billing).${items}`);
  }
  // TASK-9439 — setting OFF (warn only): the export works, but say how many
  // items are unapproved and link each one. They are NOT in the file.
  if (_apPending.length && !_ap.blocksExport) {
    warns.push(`<strong>${_apPending.length} item${_apPending.length !== 1 ? 's' : ''} awaiting approval — NOT in this file:</strong> approve them in Workforce → Claims, then Refresh, or pay them on a later run.${_apItems}`);
  }
  // TASK-9439 — denied items pay $0, and Prep says so.
  if ((_ap.denied || []).length) {
    warns.push(`<strong>Denied — paid $0:</strong> a reviewer denied these, so they are not in this file.${_ap.denied.map(x => _prepApprovalItemHTML(x, _apLabel)).join('')}`);
  }
  // TASK-1483 — vehicle claims left out of this run (no rate on file for the
  // work date, or not enrolled). Amber: the file is right for what it
  // holds, but these people may expect more.
  if ((ex.vehicleUnsettled || []).length) {
    const items = ex.vehicleUnsettled.map(x =>
      `<div style="padding:1px 0;">${escapeHtml(x.name)}: ${[x.pending ? `${x.pending} awaiting approval` : '', x.noRate ? `${x.noRate} with no mileage rate on file for that date` : '', x.ineligible ? `${x.ineligible} from someone not enrolled in ${_pvLabel()} (HR → Employment info)` : ''].filter(Boolean).join(', ')}</div>`).join('');
    warns.push(`<strong>${_pvLabel()} claims NOT in this run:</strong> approve them in Workforce → Claims (or set a rate that covers those dates), then Refresh.${items}`);
  }
  // TASK-9794 — the "Needs review" list: one row per flagged shift with a
  // one-click Accept (Chris q03 2026-10-12: WARN only — the export below still
  // works; it asks before proceeding and the run records the unreviewed count).
  // Server flags: backdated / overlap / future. Client marks: oversized shift,
  // driver-corrected clock-out, stuck shift closed late. Accept = "a human
  // looked" — it never changes hours.
  if ((ex.reviewItems || []).length) parts.push(_prepReviewListHTML(ex.reviewItems));
  else if ((ex.flagged || []).length) warns.push(`<strong>Needs review:</strong> ${ex.flagged.map(x => escapeHtml(x.name)).join(', ')} — flagged entries (oversized shift or driver-corrected clock-out). Hours are included as-is; verify before paying.`);
  // TASK-10034 — No Longer Employed: they stay on payroll through the pay
  // period that contains their last day. Name it so nobody misses a final check.
  if ((ex.finalChecks || []).length) warns.push(`<strong>Final check:</strong> ${ex.finalChecks.map(x => `${escapeHtml(x.name)} (last day ${escapeHtml(_prepFmtYmd(x.lastDay))}${x.hours > 0 ? `, ${_prepFmtHrs(x.hours)}h` : ''})`).join(', ')} — left this pay period. Run their final check.`);
  if ((ex.afterLastDay || []).length) warns.push(`<strong>Hours after the last day:</strong> ${ex.afterLastDay.map(x => `${escapeHtml(x.name)} (${x.count} shift${x.count !== 1 ? 's' : ''} after ${escapeHtml(_prepFmtYmd(x.lastDay))}${x.hours > 0 ? `, ${_prepFmtHrs(x.hours)}h` : ''})`).join(', ')} — check before paying.`);
  if (ex.finalChecksUnknown) warns.push('<strong>Could not check who left:</strong> refresh, or check final checks by hand.');
  // TASK-10040 — a day with more than 24 hours on it. Each shift counts on
  // its clock-in day, so this is a forgotten clock-out (or two overlapping
  // shifts). Warning only; the export still works and hours are unchanged.
  if ((ex.dayOver24 || []).length) {
    const _d24 = ex.dayOver24.map(x => {
      const [y, m, d] = String(x.dateKey || '').split('-').map(Number);
      const when = (y && m && d) ? _prepFmtDate(new Date(y, m - 1, d, 12).getTime(), { weekday: 'short', month: 'short', day: 'numeric' }) : String(x.dateKey || '');
      return `<span class="prep-exc-name" style="cursor:pointer;text-decoration:underline;" data-prep-pid="${escapeHtml(String(x.pid))}">${escapeHtml(x.name)}</span> ${escapeHtml(when)} (${_prepFmtHrs(x.hours)}h)`;
    }).join(', ');
    warns.push(`<strong>Over 24 hours in one day:</strong> ${_d24} — each shift counts on the day it started, so a forgotten clock-out piles onto that day. Check the clock-out in Workforce → Hours before paying.`);
  }
  if ((ex.terminatedWithHours || []).length) warns.push(`<strong>Hours on inactive/terminated:</strong> ${ex.terminatedWithHours.map(x => `${escapeHtml(x.name)} (${_prepFmtHrs(x.hours)}h)`).join(', ')} — include their final pay in Gusto.`);
  if ((ex.zeroHourActives || []).length) warns.push(`<strong>Zero hours:</strong> ${ex.zeroHourActives.map(x => escapeHtml(x.name)).join(', ')} — active hourly folks with no hours this period (forgot to clock?). They're excluded from the export.`);
  if (warns.length) {
    parts.push(`
      <div style="display:flex;gap:10px;align-items:flex-start;background:rgba(217,119,6,0.08);border:1px solid rgba(217,119,6,0.35);border-radius:10px;padding:12px 14px;margin-bottom:10px;">
        <span class="material-icons" style="color:#D97706;">warning</span>
        <div style="flex:1;min-width:0;font-size:0.85rem;color:var(--text-secondary);">${warns.map(w => `<div style="padding:2px 0;">${w}</div>`).join('')}</div>
      </div>`);
  }
  if (!parts.length) {
    parts.push(`
      <div style="display:flex;gap:10px;align-items:center;background:rgba(5,150,105,0.08);border:1px solid rgba(5,150,105,0.35);border-radius:10px;padding:10px 14px;margin-bottom:10px;">
        <span class="material-icons" style="color:#059669;">check_circle</span>
        <div style="font-size:0.88rem;color:var(--text-secondary);">No exceptions — this period is ready to export.</div>
      </div>`);
  }
  return parts.join('');
}

function _prepDrilldownHTML(row) {
  // Per-week REG/OT split (from the single-source OT helper) + per-entry days.
  const weekRows = (row.weeks || []).filter(w => !w.skipped).map(w => {
    const ws = new Date(w.key + 'T00:00:00');
    return `<div style="padding:2px 0;">Week of ${ws.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}: ${_prepFmtHrs(w.reg)} reg${w.ot > 0 ? ` + <span style="color:var(--status-danger);font-weight:600;">${_prepFmtHrs(w.ot)} OT</span>` : ''}${w.straddle ? ' <span style="color:#D97706;">(includes prior-period week hours)</span>' : ''}</div>`;
  }).join('');
  const entryRows = (row.entries || []).map(e => {
    const flags = [];
    if (!e.completed) flags.push('<span style="color:#DC2626;font-weight:700;">MISSING CLOCK-OUT</span>');
    if (e.flagged) flags.push('<span style="color:#D97706;font-weight:600;">needs review</span>');
    const job = [e.jobTitle, e.customerName].filter(Boolean).map(escapeHtml).join(' · ');
    return `
      <tr>
        <td style="white-space:nowrap;">${new Date(e.inMs).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</td>
        <td style="white-space:nowrap;">${_prepFmtTime(e.inMs)} – ${e.outMs ? _prepFmtTime(e.outMs) : '<em>open</em>'}</td>
        <td style="text-align:right;">${e.completed ? _prepFmtHrs(e.hours) : '—'}</td>
        <td>${job || ''}</td>
        <td>${flags.join(' ')}</td>
      </tr>`;
  }).join('');
  const perDiemNote = row.perDiemEligible
    // TASK-1483 — this used to say "paid as a reimbursement, never wages", a
    // tax conclusion DD drew for the customer. Chris 2026-09-28: DD does not
    // decide a tenant's tax treatment. The label states the facts — how it was
    // computed and which column it is exported in — and nothing more.
    ? `Per diem: ${row.perDiemDays} day${row.perDiemDays !== 1 ? 's' : ''} ≥ 8h × ${_prepFmtMoney(row.perDiemRate)}/day = ${_prepFmtMoney(row.perDiemDollars)} (auto-calculated from hours worked; exported in the Reimbursement column). How it is taxed is set in your payroll provider.`
    : (row.perDiemSource === 'manual' ? `Per diem: ${_prepFmtMoney(row.perDiemDollars)} from manual entries (no per-diem rate configured in HR).` : '');
  // TASK-1483 — personal-vehicle line, labelled by what is actually true.
  const _vehTotal = (row.vehicleReimb || 0) + (row.vehicleTaxable || 0);
  const vehicleNote = _vehTotal > 0.004
    ? `${_pvLabel()}: ${Number(row.vehicleMiles || 0).toFixed(1)} mi over ${row.vehicleDays} day${row.vehicleDays !== 1 ? 's' : ''} = ${_prepFmtMoney(_vehTotal)} — ${_prepFmtMoney(row.vehicleReimb || 0)} exported as a reimbursement${(row.vehicleTaxable || 0) > 0.004 ? ` + ${_prepFmtMoney(row.vehicleTaxable)} exported as wages in the Bonus column (the part of the flat daily amount the miles don't cover, per your Claims setting)` : ''}.${((row.vehicleUncovered || 0) - (row.vehicleTaxable || 0)) > 0.004 ? ` For information: ${_prepFmtMoney((row.vehicleUncovered || 0) - (row.vehicleTaxable || 0))} of the flat daily amount on days set to pay it as a reimbursement is not covered by miles × rate.` : ''}`
    : '';
  // TASK-1483 — flat daily vehicle pay: why EACH day did or didn't earn the
  // flat under the tenant's "a day qualifies when…" trigger. Reasons are
  // engine-built text (numbers + fixed words) and are escaped anyway.
  const _vDays = Array.isArray(row.vehicleDayDetail) ? row.vehicleDayDetail : [];
  const vehicleDaysNote = _vDays.length ? `
      <div style="font-size:0.78rem;color:var(--text-secondary);margin-top:4px;">
        <div style="font-weight:600;color:var(--text-primary);">${_pvLabel()} — days</div>
        ${_vDays.map(d => `<div style="padding:1px 0;${d.qualifies ? '' : 'color:#B45309;'}">${escapeHtml(d.ymd)}: ${d.hours === null || d.hours === undefined ? '—' : `${Number(d.hours).toFixed(2)}h`}, ${Number(d.miles || 0).toFixed(1)} mi — ${escapeHtml(d.reason || '')}${d.qualifies ? ` (${_prepFmtMoney(d.amount || 0)}${d.status === 'pending' ? ', awaiting approval' : d.status === 'denied' ? ', denied' : ''})` : ''}</div>`).join('')}
      </div>` : '';
  // TASK-1558 — the mid-period rate split, shown instead of silently blending.
  // Hour split is always visible; dollar figures respect the rate mask.
  let rateSplitNote = '';
  if (row.rateChanged && row.pay) {
    const p = row.pay;
    const changeLines = (p.changes || []).length && _prep.ratesVisible
      ? `<div style="padding:1px 0;">${p.changes.map(c => escapeHtml((window.PayRates?.describeChange?.(c) || '')
          + (c.enteredAfterPeriodEnd ? ' [entered after this period closed — verify vs what was paid]' : ''))).join('; ')}</div>` : '';
    const split = (p.segments || []).map(s =>
      `${_prepFmtHrs(s.hours)}h @ ${_prep.ratesVisible ? _prepFmtMoney(s.rate) : '••••'}`).join(' + ');
    const _rowMethod = (p.weeks || []).some(w => w.method === 'rate_of_work')
      ? 'with OT at 1.5x the rate of the work performed (29 CFR 778.419)'
      : 'with OT at the weighted-average regular rate (29 CFR 778.115)';
    const gross = (p.supported !== false && p.multiRate && p.gross !== null && _prep.ratesVisible)
      ? ` — est. gross ${_prepFmtMoney(p.gross)}${p.salaryDollars > 0 ? ` (incl. ${_prepFmtMoney(p.salaryDollars)} prorated salary)` : ''} ${_rowMethod}` : '';
    const handReview = p.supported === false
      ? `<div style="padding:1px 0;color:#DC2626;font-weight:600;">Review by hand: ${(p.notes || []).map(escapeHtml).join('; ')}</div>` : '';
    rateSplitNote = `
      <div style="font-size:0.8rem;color:var(--text-primary);margin-bottom:6px;border:1px solid rgba(217,119,6,0.4);border-radius:6px;padding:6px 8px;background:rgba(217,119,6,0.06);">
        <div style="font-weight:700;color:#D97706;">Pay changed inside this period</div>
        ${changeLines}
        ${split ? `<div style="padding:1px 0;">Split: ${split}${gross}</div>` : ''}
        ${handReview}
      </div>`;
  }
  // TASK-9388 — internal-time pay estimate: hours at each internal rate, and
  // for each OT week how the premium was priced. Dollars honor the rate mask.
  let internalPayNote = '';
  if (row.internalPay) {
    const ip = row.internalPay;
    const money = (n) => _prep.ratesVisible ? _prepFmtMoney(n) : '••••';
    const segs = (ip.segments || []).map(s => `${escapeHtml(s.label)} ${_prepFmtHrs(s.hours)}h @ ${money(s.rate)}`).join(' + ');
    const weeks = (ip.otWeeks || []).map(w => {
      const ws = new Date(w.key + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      const how = w.method === 'rate_of_work'
        ? `${(w.otByRate || []).map(x => `${_prepFmtHrs(x.hours)}h @ 1.5 × ${money(x.rate)}`).join(' + ')} (rate of work performed, 29 CFR 778.419)`
        : `${_prepFmtHrs(w.otHours)}h × ½ × ${money(w.weightedRate)} regular rate (weighted average, 29 CFR 778.115)`;
      return `<div style="padding:1px 0;">Week of ${ws} OT: ${how}</div>`;
    }).join('');
    internalPayNote = `
      <div style="font-size:0.8rem;color:var(--text-primary);margin-bottom:6px;border:1px solid rgba(59,130,246,0.4);border-radius:6px;padding:6px 8px;background:rgba(59,130,246,0.06);">
        <div style="font-weight:700;">Internal-time pay (Pay Settings rate)</div>
        ${segs ? `<div style="padding:1px 0;">${segs}</div>` : ''}
        ${weeks}
        ${ip.gross !== null ? `<div style="padding:1px 0;">Est. gross ${money(ip.gross)}${row.exempt ? ' (no OT — exempt)' : ''}</div>` : `<div style="padding:1px 0;color:#DC2626;font-weight:600;">Review by hand: ${(ip.notes || []).map(escapeHtml).join('; ')}</div>`}
      </div>`;
  }
  return `
    <div style="padding:10px 12px;background:var(--bg-secondary,rgba(148,163,184,0.08));border-radius:8px;">
      ${rateSplitNote}
      ${internalPayNote}
      ${row.isSalary ? '<div style="font-size:0.8rem;color:var(--text-secondary);margin-bottom:6px;">Salaried — hours shown for reference; Gusto pays salary automatically (no hours exported).</div>' : ''}
      ${weekRows ? `<div style="font-size:0.82rem;color:var(--text-secondary);margin-bottom:6px;">${weekRows}</div>` : ''}
      ${entryRows ? `
      <div style="overflow-x:auto;">
        <table style="width:100%;border-collapse:collapse;font-size:0.82rem;" class="prep-drill-table">
          <thead><tr style="color:var(--text-secondary);text-align:left;"><th style="padding:2px 8px 2px 0;">Day</th><th style="padding:2px 8px 2px 0;">Shift</th><th style="padding:2px 8px 2px 0;text-align:right;">Hrs</th><th style="padding:2px 8px 2px 0;">Job</th><th></th></tr></thead>
          <tbody>${entryRows}</tbody>
        </table>
      </div>` : '<div style="font-size:0.82rem;color:var(--text-muted);">No clock entries this period.</div>'}
      ${perDiemNote ? `<div style="font-size:0.8rem;color:var(--text-secondary);margin-top:6px;">${perDiemNote}</div>` : ''}
      ${vehicleNote ? `<div style="font-size:0.8rem;color:var(--text-secondary);margin-top:6px;">${vehicleNote}</div>` : ''}
      ${vehicleDaysNote}
    </div>`;
}

function renderPrep() {
  const el = document.getElementById('payroll-prep-root');
  if (!el) return;
  // TASK-9178 (COD) — exception-name links carry the pid in a data attribute
  // and are handled here, never as a pid spliced into an inline onclick JS
  // string (encodeURIComponent leaves ' intact, so a pid with an apostrophe
  // broke out of the string). Bound once per root element.
  if (!el.__prepPidBound && typeof el.addEventListener === 'function') {
    el.__prepPidBound = true;
    el.addEventListener('click', (ev) => {
      // TASK-9439 — "Review →" on an approval item opens Workforce → Claims.
      const cl = ev.target && ev.target.closest ? ev.target.closest('[data-prep-claim-pid]') : null;
      if (cl && el.contains(cl)) { ev.preventDefault(); prepOpenClaim(cl.getAttribute('data-prep-claim-pid'), cl.getAttribute('data-prep-claim-ymd')); return; }
      // TASK-9794 — Accept a flagged shift (entry id in a data attribute, never
      // spliced into an inline onclick string).
      const ac = ev.target && ev.target.closest ? ev.target.closest('[data-prep-accept]') : null;
      if (ac && el.contains(ac)) { ev.preventDefault(); prepAcceptReview(ac.getAttribute('data-prep-accept')); return; }
      const t = ev.target && ev.target.closest ? ev.target.closest('[data-prep-pid]') : null;
      if (t && el.contains(t)) prepShowEmployeeByPid(t.getAttribute('data-prep-pid'));
    });
  }

  const header = `
    <div style="display:flex;align-items:center;justify-content:space-between;gap:10px;flex-wrap:wrap;margin-bottom:10px;">
      <div>
        <div style="display:flex;align-items:center;gap:8px;font-weight:700;font-size:1.05rem;color:var(--text-primary);">
          <span class="material-icons" style="color:var(--brand-blue);">fact_check</span> Payroll Prep
        </div>
        <div style="font-size:0.8rem;color:var(--text-secondary);">Review the pay period, resolve exceptions, then import the hours into Gusto.</div>
      </div>
      <button class="btn btn-secondary btn-sm" onclick="window.PayrollModule.prepRefresh()" title="Reload this period">
        <span class="material-icons" style="font-size:16px;">refresh</span>
      </button>
    </div>`;

  if (_prep.loading) {
    el.innerHTML = `<div class="card"><div style="padding:8px 12px;">${header}
      <div style="padding:28px;text-align:center;color:var(--text-secondary);">
        <span class="material-icons" style="animation:spin 1s linear infinite;display:inline-block;">progress_activity</span>
        <div style="margin-top:8px;font-size:0.88rem;">Building pay-period rollup…</div>
      </div></div></div>`;
    return;
  }
  if (_prep.error || !_prep.data) {
    el.innerHTML = `<div class="card"><div style="padding:8px 12px;">${header}
      <div class="empty-state">
        <div class="empty-state-icon">⚠️</div>
        <div class="empty-state-title">Couldn't build the pay period</div>
        <div class="empty-state-text">${escapeHtml(_prep.error || 'Please try again.')}</div>
        <button class="btn btn-secondary" style="margin-top:12px;" onclick="window.PayrollModule.prepRefresh()">Retry</button>
      </div></div></div>`;
    return;
  }

  const data = _prep.data;
  const label = `${_prepFmtDate(data.startMs, { month: 'short', day: 'numeric' })} – ${_prepFmtDate(data.endMs)}`;
  const periodTypeLabel = { weekly: 'Weekly', biweekly: 'Bi-weekly', semimonthly: 'Semi-monthly', monthly: 'Monthly' }[data.periodType] || data.periodType;
  const periodNav = `
    <div style="display:flex;align-items:center;gap:8px;flex-wrap:wrap;margin-bottom:10px;">
      <button class="btn btn-secondary btn-sm" onclick="window.PayrollModule.prepNav(-1)" title="Previous period"><span class="material-icons" style="font-size:16px;">chevron_left</span></button>
      <div style="font-weight:600;color:var(--text-primary);">${label}</div>
      <button class="btn btn-secondary btn-sm" onclick="window.PayrollModule.prepNav(1)" ${_prep.offset >= 0 ? 'disabled' : ''} title="Next period"><span class="material-icons" style="font-size:16px;">chevron_right</span></button>
      <span style="font-size:0.78rem;color:var(--text-muted);">${periodTypeLabel} period${_prep.offset === 0 ? ' · in progress' : ''}</span>
    </div>`;

  const crit = data.exceptions?.criticalCount || 0;
  // TASK-9439 — the tenant's "block until approved" setting reuses the same
  // disable path as missing clock-outs.
  const apBlock = _prepApprovalBlock();
  const exportDisabled = crit > 0 || apBlock > 0 || _prep.lockLoading;
  const exportDisabledWhy = crit > 0 ? 'Resolve the missing clock-outs first' : (apBlock > 0 ? 'Approve or deny the items awaiting approval first' : 'Checking earlier pay periods for changes…');

  // ── Rollup table ──────────────────────────────────────────────────────────
  const eye = `<button onclick="window.PayrollModule.prepToggleRates()" title="${_prep.ratesVisible ? 'Hide' : 'Show'} pay rates" style="background:none;border:none;cursor:pointer;color:var(--text-secondary);vertical-align:middle;padding:0 2px;"><span class="material-icons" style="font-size:16px;">${_prep.ratesVisible ? 'visibility_off' : 'visibility'}</span></button>`;
  let tReg = 0, tOT = 0, tTotal = 0, tPerDiem = 0, tBonus = 0, tReimb = 0, tVeh = 0, tVehTax = 0;
  // TASK-1483 — the two vehicle columns appear only when the period has any,
  // so every other tenant's table is unchanged.
  const _pvCols = data.rows.some(r => (r.vehicleReimb || 0) > 0.004 || (r.vehicleTaxable || 0) > 0.004 || r.vehiclePending > 0 || r.vehicleNoRate > 0);
  const _colN = _pvCols ? 10 : 8;
  const bodyRows = data.rows.map((r, i) => {
    tReg += r.reg; tOT += r.ot; tTotal += r.total; tPerDiem += r.perDiemDollars; tBonus += r.bonus; tReimb += r.reimbursement;
    tVeh += r.vehicleReimb || 0; tVehTax += r.vehicleTaxable || 0;
    const badges = [];
    if (r.missingClockOuts > 0) badges.push('<span style="color:#DC2626;font-weight:700;font-size:0.72rem;border:1px solid rgba(220,38,38,0.5);border-radius:4px;padding:0 4px;">OPEN SHIFT</span>');
    if (r.flaggedEntries > 0) badges.push('<span style="color:#D97706;font-weight:600;font-size:0.72rem;border:1px solid rgba(217,119,6,0.5);border-radius:4px;padding:0 4px;">REVIEW</span>');
    // TASK-1558 — mid-period pay change badge (no dollar values — mask-safe).
    if (r.rateChanged) badges.push('<span style="color:#D97706;font-weight:700;font-size:0.72rem;border:1px solid rgba(217,119,6,0.5);border-radius:4px;padding:0 4px;" title="Pay changed inside this period — expand the row for the split">RATE CHANGED</span>');
    if (r.lastDayYmd) badges.push(`<span style="color:#B91C1C;font-weight:700;font-size:0.72rem;border:1px solid rgba(185,28,28,0.5);border-radius:4px;padding:0 4px;">LAST DAY ${escapeHtml(_prepFmtYmd(r.lastDayYmd).toUpperCase())}</span>`); // TASK-10034
    else if (r.inactive) badges.push('<span style="color:var(--text-muted);font-size:0.72rem;border:1px solid var(--border-color,rgba(148,163,184,0.4));border-radius:4px;padding:0 4px;">INACTIVE</span>');
    // TASK-4633 — a contractor appears in the rollup (their hours are real and
    // job-costable) but is NOT in the Gusto employee file below. Say which.
    if (r.workerType === 'contractor') badges.push('<span style="color:#0F766E;font-weight:700;font-size:0.72rem;border:1px solid rgba(15,118,110,0.5);border-radius:4px;padding:0 4px;" title="1099 contractor — excluded from the payroll import; pay via Gusto → Pay contractors">CONTRACTOR</span>');
    // TASK-4633 — worked, no rate, nobody has classified them. Exports as W-2.
    if (!r.isSalary && !r.hasRate && !r.workerTypeExplicit && (r.total > 0.004 || r.rawHours > 0.004)) badges.push('<span style="color:#DC2626;font-weight:700;font-size:0.72rem;border:1px solid rgba(220,38,38,0.5);border-radius:4px;padding:0 4px;" title="No pay rate and no worker type — this row exports as a W-2 employee">UNCLASSIFIED</span>');
    // TASK-9178 — the old badge fired only on ABSENCE. A contradictory record
    // (one field W-2, the other 1099) fires regardless of rate: the Tax-tab
    // value decided which file this row is in, and a human should confirm it.
    if (r.workerClassConflict) badges.push(`<span style="color:#DC2626;font-weight:700;font-size:0.72rem;border:1px solid rgba(220,38,38,0.5);border-radius:4px;padding:0 4px;" title="Two classification fields disagree on this person. Treated as ${r.workerType === 'contractor' ? '1099 contractor (not in the payroll file)' : 'W-2 employee (in the payroll file)'} per the Tax tab. To clear: HR → Tax tab → Edit → pick their classification from the list → Save">CLASS CONFLICT</span>`);
    // TASK-1558 — a rate that changed mid-period never renders as one flat
    // number: show both endpoints (old → new) when rates are visible.
    const _rateChangedCell = r.rateChanged && !r.isSalary && r.pay?.segments?.length > 1
      ? r.pay.segments.map(s => _prepFmtMoney(s.rate)).join(' → ')
      : null;
    const rateCell = _prep.ratesVisible
      ? (r.isSalary ? 'Salary' : (_rateChangedCell || (r.rate > 0 ? _prepFmtMoney(r.rate) : '<span style="color:#D97706;" title="No pay rate on file in DispatchDeck — Gusto’s rate will apply">—</span>')))
      : '<span style="letter-spacing:1px;color:var(--text-muted);">••••</span>';
    const expanded = !!_prep.expanded[i];
    return `
      <tr${(r.missingClockOuts > 0) ? ' style="background:rgba(220,38,38,0.05);"' : ''}>
        <td style="text-align:left;white-space:nowrap;">
          <span style="cursor:pointer;display:inline-flex;align-items:center;gap:6px;" onclick="window.PayrollModule.prepToggleRow(${i})">
            <span class="material-icons" style="font-size:16px;color:var(--text-muted);">${expanded ? 'expand_more' : 'chevron_right'}</span>
            <span style="font-weight:500;color:var(--text-primary);">${escapeHtml(r.name)}</span>
          </span>
          ${badges.join(' ')}
        </td>
        <td style="text-align:right;white-space:nowrap;">${rateCell}</td>
        <td style="text-align:right;">${r.reg > 0 ? _prepFmtHrs(r.reg) : '-'}</td>
        <td style="text-align:right;${r.ot > 0 ? 'color:var(--status-danger);font-weight:600;' : ''}">${r.ot > 0 ? _prepFmtHrs(r.ot) : '-'}</td>
        <td style="text-align:right;font-weight:600;">${r.total > 0 ? _prepFmtHrs(r.total) : '-'}</td>
        <td style="text-align:right;" title="${r.perDiemEligible ? `${r.perDiemDays} day(s) ≥ 8h × ${_prepFmtMoney(r.perDiemRate)}/day (auto)` : (r.perDiemSource === 'manual' ? 'Manual per-diem entries' : '')}">${r.perDiemDollars > 0 ? _prepFmtMoney(r.perDiemDollars) : '-'}</td>
        <td style="text-align:right;">${r.bonus > 0 ? _prepFmtMoney(r.bonus) : '-'}</td>
        <td style="text-align:right;">${r.reimbursement > 0 ? _prepFmtMoney(r.reimbursement) : '-'}</td>
        ${_pvCols ? `<td style="text-align:right;" title="${(r.vehicleMiles || 0).toFixed(1)} mi — exported as a reimbursement${r.vehiclePending ? `; ${r.vehiclePending} claim(s) awaiting approval, not included` : ''}">${(r.vehicleReimb || 0) > 0.004 ? _prepFmtMoney(r.vehicleReimb) : '-'}${r.vehiclePending || r.vehicleNoRate ? ' <span style="color:#D97706;">*</span>' : ''}</td>
        <td style="text-align:right;${(r.vehicleTaxable || 0) > 0.004 ? 'color:#B45309;' : ''}" title="Part of a flat daily amount the miles don't cover, exported as wages (only when your Claims setting says so)">${(r.vehicleTaxable || 0) > 0.004 ? _prepFmtMoney(r.vehicleTaxable) : '-'}</td>` : ''}
      </tr>
      ${expanded ? `<tr><td colspan="${_colN}" style="padding:4px 8px 10px;">${_prepDrilldownHTML(r)}</td></tr>` : ''}`;
  }).join('');

  const table = `
    <div style="overflow-x:auto;">
      <table class="weekly-hours-table" style="width:100%;">
        <thead>
          <tr>
            <th style="text-align:left;">Employee</th>
            <th style="text-align:right;white-space:nowrap;">Rate ${eye}</th>
            <th style="text-align:right;">Reg</th>
            <th style="text-align:right;">OT</th>
            <th style="text-align:right;">Total</th>
            <th style="text-align:right;">Per Diem</th>
            <th style="text-align:right;">Bonus</th>
            <th style="text-align:right;">Reimb.</th>
            ${_pvCols ? `<th style="text-align:right;" title="${_pvLabel()} exported as a reimbursement">${_pvLabel()}</th>
            <th style="text-align:right;" title="${_pvLabel()} exported as wages (Bonus) — only when your Claims setting says so">${_pvLabel()} (as wages)</th>` : ''}
          </tr>
        </thead>
        <tbody>${bodyRows || `<tr><td colspan="${_colN}" style="text-align:center;padding:16px;color:var(--text-secondary);">No people or hours this period.</td></tr>`}</tbody>
        <tfoot>
          <tr class="wh-totals-row">
            <td style="text-align:left;font-weight:700;">Total</td>
            <td></td>
            <td style="text-align:right;font-weight:600;">${tReg > 0 ? _prepFmtHrs(tReg) : '-'}</td>
            <td style="text-align:right;font-weight:600;">${tOT > 0 ? _prepFmtHrs(tOT) : '-'}</td>
            <td style="text-align:right;font-weight:700;">${tTotal > 0 ? _prepFmtHrs(tTotal) : '-'}</td>
            <td style="text-align:right;font-weight:600;">${tPerDiem > 0 ? _prepFmtMoney(tPerDiem) : '-'}</td>
            <td style="text-align:right;font-weight:600;">${tBonus > 0 ? _prepFmtMoney(tBonus) : '-'}</td>
            <td style="text-align:right;font-weight:600;">${tReimb > 0 ? _prepFmtMoney(tReimb) : '-'}</td>
            ${_pvCols ? `<td style="text-align:right;font-weight:600;">${tVeh > 0.004 ? _prepFmtMoney(tVeh) : '-'}</td>
            <td style="text-align:right;font-weight:600;">${tVehTax > 0.004 ? _prepFmtMoney(tVehTax) : '-'}</td>` : ''}
          </tr>
        </tfoot>
      </table>
    </div>`;

  // ── Export section (S4) — Gusto preview doubles as the copy-paste fallback ─
  const gustoRows = buildGustoRows();
  // TASK-4627 — when per diem AND another reimbursement both exist for the
  // same person, the file shows only their sum. Show the arithmetic on screen
  // so the combined number is always reconcilable (and so nobody concludes a
  // reimbursement went missing).
  const _reimbSplit = _prepReimbSplitRows();
  const reimbSplitNote = _reimbSplit.length ? `
      <div style="margin-top:10px;font-size:0.78rem;color:var(--text-secondary);border:1px solid var(--border-color,rgba(148,163,184,0.25));border-radius:8px;padding:8px 10px;">
        <div style="font-weight:600;color:var(--text-primary);margin-bottom:3px;">How the Reimbursement column adds up</div>
        <div style="color:var(--text-muted);margin-bottom:4px;">Gusto has one Reimbursement field, so per diem and other reimbursements are combined. Both amounts ARE included:</div>
        ${_reimbSplit.map(x => `<div style="padding:1px 0;">${escapeHtml(x.name)}: ${[
          x.perDiem > 0.004 ? `${_prepFmtMoney(x.perDiem)} per diem` : '',
          x.other > 0.004 ? `${_prepFmtMoney(x.other)} other` : '',
          x.vehicle > 0.004 ? `${_prepFmtMoney(x.vehicle)} ${_pvLabel()}` : '',
        ].filter(Boolean).join(' + ')} = <strong>${_prepFmtMoney(x.total)}</strong></div>`).join('')}
      </div>` : '';
  // TASK-1483 — vehicle pay the tenant chose to pay as wages is inside Bonus.
  const _bonusSplit = _prepBonusSplitRows();
  const bonusSplitNote = _bonusSplit.length ? `
      <div style="margin-top:10px;font-size:0.78rem;color:var(--text-secondary);border:1px solid rgba(180,83,9,0.35);border-radius:8px;padding:8px 10px;">
        <div style="font-weight:600;color:var(--text-primary);margin-bottom:3px;">${_pvLabel()} set to be paid as wages is in the Bonus column</div>
        <div style="color:var(--text-muted);margin-bottom:4px;">Your Claims setting pays the part of the flat daily amount the miles don't cover as wages, so it is exported in Bonus. If you run it as its own earning type in Gusto, move these amounts there:</div>
        ${_bonusSplit.map(x => `<div style="padding:1px 0;">${escapeHtml(x.name)}: ${x.bonus > 0.004 ? `${_prepFmtMoney(x.bonus)} bonus + ` : ''}${_prepFmtMoney(x.vehicle)} ${_pvLabel()} (as wages) = <strong>${_prepFmtMoney(x.total)}</strong></div>`).join('')}
      </div>` : '';
  const previewRows = gustoRows.map(r => `<tr>${r.map((c, ci) => `<td style="padding:3px 10px 3px 0;${ci >= 2 ? 'text-align:right;' : ''}white-space:nowrap;">${escapeHtml(c)}</td>`).join('')}</tr>`).join('');
  const exportSection = `
    <div style="margin-top:14px;border-top:1px solid var(--border-color,rgba(148,163,184,0.25));padding-top:12px;">
      <div style="display:flex;align-items:center;justify-content:space-between;gap:10px;flex-wrap:wrap;margin-bottom:8px;">
        <div style="font-weight:600;color:var(--text-primary);">Send to Gusto</div>
        <div style="display:flex;gap:8px;">
          <button class="btn btn-secondary btn-sm" onclick="window.PayrollModule.prepCopyTSV()" ${exportDisabled ? `disabled title="${exportDisabledWhy}"` : 'title="Copy the table below to paste into Gusto or a spreadsheet"'}>
            <span class="material-icons" style="font-size:16px;">content_copy</span> Copy table
          </button>
          <button class="btn btn-primary btn-sm" onclick="window.PayrollModule.prepDownloadCSV()" ${exportDisabled ? `disabled title="${exportDisabledWhy}"` : 'title="CSV for Gusto: Run payroll → Import payroll data"'}>
            <span class="material-icons" style="font-size:16px;">download</span> Download Gusto CSV
          </button>
        </div>
      </div>
      ${!_prepCanRecordRun() ? `<div style="font-size:0.8rem;color:#B45309;margin-bottom:8px;">Your login does not have Workforce access, so this export will not be recorded as a payroll run and hour changes made afterwards will not be listed. Ask the account owner to add Workforce to your access.</div>` : ''}
      ${crit > 0 ? `<div style="font-size:0.8rem;color:#DC2626;margin-bottom:8px;">Export is disabled until every missing clock-out above is resolved — open shifts have no settled hours.</div>` : ''}
      ${apBlock > 0 ? `<div style="font-size:0.8rem;color:#DC2626;margin-bottom:8px;">Export is disabled until the ${apBlock} item${apBlock !== 1 ? 's' : ''} awaiting approval above ${apBlock !== 1 ? 'are' : 'is'} approved or denied (your "Approval before payroll" setting).</div>` : ''}
      ${(data.exceptions?.unreviewedCount || 0) > 0 ? `<div style="font-size:0.8rem;color:#B45309;margin-bottom:8px;">${data.exceptions.unreviewedCount} flagged shift${data.exceptions.unreviewedCount !== 1 ? 's' : ''} still need${data.exceptions.unreviewedCount !== 1 ? '' : 's'} review — the export will ask before proceeding and the hours go out as recorded.</div>` : ''}
      ${gustoRows.length ? `
      <div style="overflow-x:auto;border:1px solid var(--border-color,rgba(148,163,184,0.25));border-radius:8px;padding:8px 10px;">
        <table style="border-collapse:collapse;font-size:0.82rem;color:var(--text-primary);min-width:640px;">
          <thead><tr>${GUSTO_CSV_HEADERS.map((h, ci) => `<th style="text-align:${ci >= 2 ? 'right' : 'left'};padding:3px 10px 5px 0;color:var(--text-secondary);font-weight:600;white-space:nowrap;">${escapeHtml(h)}</th>`).join('')}</tr></thead>
          <tbody>${previewRows}</tbody>
        </table>
      </div>` : `<div style="font-size:0.82rem;color:var(--text-muted);">Nothing to export this period.</div>`}
      ${reimbSplitNote}
      ${bonusSplitNote}
      <div style="margin-top:10px;font-size:0.76rem;color:var(--text-muted);">
        In Gusto: <strong>Run payroll → Import payroll data</strong>, upload this file (or paste the copied table into a sheet and upload that) — Gusto matches employees by name and lets you map any column. Hours are decimals.
        Per diem is exported in the <strong>Reimbursement</strong> column, not as hours — and because Gusto has a single Reimbursement field, per diem and any other reimbursement are added together into the one <strong>Reimbursement</strong> column. The split stays on the table above. Pay rates are not in the file — Gusto uses its own rates.
        Salaried employees export no hours (Gusto pays salary automatically). A rate that changed mid-period shows both rates and the exact hour split above (expand the row) — update the rate in Gusto and apply that split there.
        ${data.straddleNote ? '<br><strong>Note:</strong> overtime this period includes hours earned during the prior pay period (a workweek spans the period boundary).' : ''}
      </div>
    </div>`;

  el.innerHTML = `
    <div class="card">
      <div style="padding:10px 12px;">
        ${header}
        ${periodNav}
        ${_prepLockBanner()}
        <div id="prep-rc"></div>
        ${_prepExceptionsBanner(data)}
        ${table}
        ${exportSection}
        ${_prepPaychexSectionHTML(exportDisabled, exportDisabledWhy)}
      </div>
    </div>`;
  // TASK-9952 — refunds that post here as a next-period correction (lazy chunk, read only).
  __ddChunkWrap(import('./payrollRefundCorrections.js?v=1')).then(m => m.mount(_prep.tenantId, data.startMs, data.endMs, httpsCallable(functions, 'listRefundCorrections'))).catch(() => {});
}

// Banner names click through to the Workforce employee detail (pid-keyed —
// banner rows aren't index-aligned with the table).
function prepShowEmployeeByPid(pid) {
  try { window.ClockModule?.showEmployeeDetail?.(pid); } catch (_) { /* no-op outside clock view */ }
}

// TASK-1483 — the tenant's own pay-type name ("Toothpaste pay" is fine),
// carried on the prep data by buildPayrollPrepData. Returned ALREADY ESCAPED
// (escapeHtml escapes quotes too, so it is safe in text AND attribute context);
// never pass it through escapeHtml again and never put it in an onclick.
function _pvLabel() {
  return escapeHtml(_prep.data?.vehiclePayTypeName || window.VehicleReimb?.DEFAULT_PAY_TYPE_NAME || 'Vehicle Reimbursement');
}

function escapeHtml(s) {
  return String(s == null ? '' : s)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

// Cross-module access per Pitfall #1 — window global, never ?v= import.
// TASK-1066 — Payroll now lives as a Workforce sub-tab (Chris 2026-07-09: the
// left nav was getting crowded + Workforce/payroll are synonymous). clock.js
// gates the tab on isPayrollAdmin so the owner/admin check has ONE source.
// TASK-1169 — prep* handlers power the Payroll Prep screen's inline onclicks.
window.PayrollModule = {
  initPayroll, isPayrollAdmin,
  prepNav, prepRefresh, prepToggleRates, prepToggleRow,
  prepShowEmployee, prepShowEmployeeByPid, prepDownloadCSV, prepCopyTSV,
  prepOpenClaim, // TASK-9439
  prepAcceptReview, // TASK-9794
  prepDownloadPaychex, prepOpenPaychexSetup, loadPaychexSettings, // TASK-9804
};

function __ddChunkWrap(p){var h=globalThis.__ddChunk;return typeof h==="function"?h(p):p}
